import { apiClient } from '../../api/client'
import { referenceRings, countOutside } from '../../utils/geoReference'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Textarea } from '@chakra-ui/react'
import { Box, ConfirmDialog, Flex, Text, toaster } from '@minetrace/ui'
import { useFormColors, WizardSidebar } from '@minetrace/ui/forms'
import { useNavigate, useParams } from 'react-router-dom'
import {
  LuArrowLeft,
  LuChevronDown,
  LuChevronRight,
  LuFingerprint as Fingerprint,
  LuShieldCheck as ShieldCheck,
  LuMountain as Mountain,
  LuFileText as FileText,
  LuFactory as Factory,
  LuTriangleAlert as AlertTriangle,
  LuLandmark as Landmark,
  LuUsers as Users,
  LuSignature as Signature,
  LuRefreshCw,
  LuSave,
  LuSend,
  LuShield,
  LuX,
} from 'react-icons/lu'
import { modificationRequestService, proveedoresService, componentTypesService, personsService } from '../../api/services'
import type {
  DraftChangeResponse,
  ModificationRequestResponse,
} from '../../api/models/response/modificationRequest'
import type {
  ProveedorResponse,
  IgafomRecordResponse,
  LegalDocumentRecordResponse,
  BankAccountResponse,
  MiningRightResponse,
  ProductionDeclarationResponse,
  MiningRightComponentResponseItem,
  TaxReportResponse,
  PowerValidityResponse,
  SupplierContractResponse,
  ExplosiveAuthorizationResponse,
  ExplosiveHandlerResponse,
} from '../../api/models/response/proveedor'
import type { CompanyPersonResponse } from '../../api/models/response/company'
import type { AddCompanyPersonRequest } from '../../api/models/request/company'
import type {
  AddIgafomRecordRequest,
  AddLegalDocumentRecordRequest,
  AddProductionDeclarationRequest,
  AddTaxReportRequest,
  AddPowerValidityRequest,
  AddExplosiveAuthorizationRequest,
  AddExplosiveHandlerRequest,
} from '../../api/models/request/proveedor'
import { brand, themeColors } from '../../theme'
import { PermissionGate } from '../../auth/PermissionGate'
import {
  IGAFOM_DOCUMENT_STATUSES, IGAFOM_TYPES, MINERAL_SUBSTANCES, BANK_ENTITIES, ACCOUNT_TYPES, PRODUCTION_PERIODS,
  INCOME_TAX_CATEGORIES, MINING_CATEGORIES, ALL_MINING_METHODS, LEGAL_DOCUMENT_TYPES, EXPLOSIVE_AUTHORIZATION_TYPES, MINING_RIGHT_STATUSES, enumLabel,
} from '../../data/enums'
import { resolveUbigeoLabel } from '../../lib/ubigeo'
import { ModificationModeProvider } from '../../lib/modificationMode'
import { isPointInGeoJson } from '../../lib/pointInGeoJson'
import { utmToLatLng, getUtmZoneFromGeoJson } from '../../utils/utmConverter'
import { dependentPowerValidities, extractStagedCompany, findStagedInsert, keepStagedValue, stagedFileRef, stagedPayload } from '../../lib/modificationStaging'
import Step1Identity from './steps/Step1Identity'
import PowerValiditiesSection from './PowerValiditiesSection'
import Step2Accreditation from './steps/Step2Accreditation'
import Step2bContrato from './steps/Step2bContrato'
import Step3MiningRights from './steps/Step3MiningRights'
import Step4Igafom from './steps/Step4Igafom'
import Step4bLegalDocuments from './steps/Step4bLegalDocuments'
import Step5Production from './steps/Step5Production'
import Step7Iqbf from './steps/Step7Iqbf'
import Step8Finance from './steps/Step8Finance'
import Step10Ubo from './steps/Step10Ubo'
import type { AddShareholderRequest } from '../../api/models/request/proveedor'
import type { ShareholderResponse } from '../../api/models/response/proveedor'

type TabType = 'identidad' | 'acreditacion' | 'contrato' | 'derechos' | 'igafom' | 'legaldocs' | 'produccion' | 'iqbf' | 'finanzas' | 'accionistas'

// 'accionistas' solo para sociedades (S.A./S.A.C.); se filtra del orden dentro del componente.
const BASE_TAB_ORDER: TabType[] = ['identidad', 'acreditacion', 'contrato', 'derechos', 'igafom', 'legaldocs', 'produccion', 'iqbf', 'finanzas', 'accionistas']

function isCorporationType(taxpayerType?: string | null): boolean {
  if (!taxpayerType) return false
  const t = taxpayerType.toUpperCase()
  if (t.includes('SOCIEDAD ANONIMA') || t.includes('SOCIEDAD ANÓNIMA')) return true
  const compact = t.replace(/[.\s]/g, '')
  return compact.includes('SAC') || compact.includes('SAA')
}

const LEGAL_CONDITIONS: { value: number; label: string }[] = [
  { value: 1, label: 'Formalizado' },
  { value: 2, label: 'Vía de Formalización' },
]

const OTS_STATUSES: { value: number; label: string }[] = [
  { value: 1, label: 'Vigente' },
  { value: 2, label: 'Vencido' },
  { value: 3, label: 'En Trámite' },
]

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StepFormRef = { trigger: () => Promise<boolean>; getValues: () => any } | null

export default function ModificarWizardPage() {
  const { id: proveedorId, mrId } = useParams<{ id: string; mrId: string }>()
  const navigate = useNavigate()
  const c = useFormColors()

  const [mr, setMr] = useState<ModificationRequestResponse | null>(null)
  // Comentario del revisor. El backend lo guarda (ReviewNotes) y el historial lo muestra,
  // pero la UI nunca lo enviaba: una solicitud rechazada no decía por qué.
  const [reviewNotes, setReviewNotes] = useState('')
  const [proveedor, setProveedor] = useState<ProveedorResponse | null>(null)
  const [changes, setChanges] = useState<DraftChangeResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [activeTab, setActiveTab] = useState<TabType>('identidad')
  const [confirming, setConfirming] = useState<'cancel' | 'approve' | 'reject' | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [refreshResult, setRefreshResult] = useState<'diffs' | 'no-diffs' | null>(null)
  const [refreshingDms, setRefreshingDms] = useState(false)
  const [refreshDmsResult, setRefreshDmsResult] = useState<'diffs' | 'no-diffs' | null>(null)

  const activeStepRef = useRef<StepFormRef>(null)
  // Catálogo id→nombre de tipos de componente, para mostrar el nombre (no el GUID) en los diffs.
  const [componentTypeNames, setComponentTypeNames] = useState<Record<string, string>>({})

  // Solo la PRIMERA carga muestra el spinner de página completa. Las recargas tras
  // stagear un cambio (add/remove de componentes, etc.) son silenciosas: si pusiéramos
  // loading=true, el `if (loading) return <spinner>` desmontaría toda la página y cerraría
  // los modales abiertos (p. ej. el de componentes), impidiendo hacer varios cambios seguidos.
  const loadedOnceRef = useRef(false)
  const load = useCallback(async () => {
    if (!mrId || !proveedorId) return
    const initial = !loadedOnceRef.current
    if (initial) setLoading(true)
    try {
      const [mrData, pData, chData] = await Promise.all([
        modificationRequestService.getById(mrId),
        proveedoresService.getById(proveedorId),
        modificationRequestService.listDraftChanges(mrId),
      ])
      setMr(mrData)
      setProveedor(pData)
      setChanges(chData)
      loadedOnceRef.current = true
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'Error al cargar la solicitud' })
    } finally {
      if (initial) setLoading(false)
    }
  }, [mrId, proveedorId])

  useEffect(() => { load() }, [load])

  // Catálogo de tipos de componente (explotación) para resolver nombres en los diffs.
  useEffect(() => {
    let cancelled = false
    componentTypesService.getAll({ categoryCode: 'explotacion', pageSize: 200 })
      .then(res => {
        if (cancelled) return
        const map: Record<string, string> = {}
        for (const it of res.items) map[it.id] = it.name
        setComponentTypeNames(map)
      })
      .catch(() => { /* silencioso — el diff cae al GUID si no se pudo cargar */ })
    return () => { cancelled = true }
  }, [])

  const isDraft = mr?.requestStatus === 0
  const isPending = mr?.requestStatus === 1
  const reviewMode = !isDraft

  // ── Overlays ──────────────────────────────────────────────────────────────
  const overlaidMiningRights = applyMiningRightsOverlay(proveedor?.miningRights ?? [], changes)
  const overlaidBankAccounts = applyBankAccountOverlay(proveedor?.bankAccounts ?? [], changes)
  const overlaidMiningSupplier = applyMiningSupplierOverlay(proveedor, changes)
  const overlaidPersons = applyPersonsOverlay(proveedor?.persons ?? [], changes)
  const stagedCompany = extractStagedCompany(changes)
  const overlaidTaxReports = applyTaxReportsOverlay(proveedor?.taxReports ?? [], changes)
  const overlaidPowerValidities = applyPowerValiditiesOverlay(proveedor?.powerValidities ?? [], changes, overlaidPersons)
  const overlaidShareholders = applyShareholdersOverlay(proveedor?.shareholders ?? [], changes)
  const overlaidContract = applyContractOverlay(proveedor?.activeContract ?? null, proveedor?.contractHistory ?? [], changes)

  // Accionistas (UBO) — solo sociedades S.A./S.A.C.
  const isCorporation = proveedor?.isCorporation ?? isCorporationType(proveedor?.taxpayerType)
  const TAB_ORDER = isCorporation ? BASE_TAB_ORDER : BASE_TAB_ORDER.filter(tab => tab !== 'accionistas')
  const requiresEnhancedDueDiligence = overlaidShareholders.some(s => s.validationStatus !== 1 && s.validationStatus !== 0)

  // ── Accionistas (UBO) staging ───────────────────────────────────────────────
  const buildShareholderSnapshot = (
    data: AddShareholderRequest,
    cert: { fileAttachmentId: string; validationStatus: number; validationStatusName: string; validationMessage: string | null } | null,
    fileName: string | null,
  ) => JSON.stringify({
    companyBusinessId: proveedor?.companyBusinessId ?? null,
    documentType: data.documentType,
    documentNumber: data.documentNumber,
    firstName: data.firstName,
    lastName: data.lastName,
    participationPercent: data.participationPercent,
    certificateFileAttachmentId: cert?.fileAttachmentId ?? null,
    certificateFileName: fileName,
    validationStatus: cert?.validationStatus ?? 0,
    validationStatusName: cert?.validationStatusName ?? 'PENDIENTE',
    validationMessage: cert?.validationMessage ?? null,
  })

  // Alta "PDF primero": sube y valida el CUL (que resuelve DNI + nombre por RENIEC)
  // y stagea el accionista sin que el usuario digite identidad.
  const stageShareholderAddFromCertificate = async (file: File, pct: number) => {
    if (!mrId || !proveedorId) return
    const cert = await proveedoresService.validateShareholderCertificate(proveedorId, file)
    if (!cert.documentNumber) {
      toaster.error({ description: 'No se pudo leer el N° de documento del certificado. Suba el PDF original del CUL.' })
      return
    }
    await modificationRequestService.upsertDraftChange(mrId, {
      entityName: 'SupplierShareholder', entityId: null, operation: 0,
      proposedSnapshotJson: buildShareholderSnapshot(
        { documentType: cert.documentType, documentNumber: cert.documentNumber, firstName: cert.firstName, lastName: cert.lastName, participationPercent: pct },
        cert, file.name),
    })
    toaster.success({ description: 'Accionista agregado al staging' })
    await load()
  }

  const stageShareholderUpdate = async (shId: string, data: AddShareholderRequest, file?: File) => {
    if (!mrId || !proveedorId) return
    const cert = file ? await proveedoresService.validateShareholderCertificate(proveedorId, file) : null
    if (shId.startsWith('staged-')) {
      // Sin PDF nuevo se conserva el certificado ya validado del alta stageada (antes se
      // perdía y el accionista volvía a "PENDIENTE").
      const previous = stagedPayload(changes, shId)
      const keptCert = cert ?? (previous?.certificateFileAttachmentId
        ? {
            fileAttachmentId: String(previous.certificateFileAttachmentId),
            validationStatus: Number(previous.validationStatus ?? 0),
            validationStatusName: String(previous.validationStatusName ?? 'PENDIENTE'),
            validationMessage: (previous.validationMessage as string | null) ?? null,
          }
        : null)
      const keptName = file?.name ?? keepStagedValue<string>(null, previous, 'certificateFileName')
      await modificationRequestService.removeDraftChange(mrId, shId.substring('staged-'.length))
      await modificationRequestService.upsertDraftChange(mrId, {
        entityName: 'SupplierShareholder', entityId: null, operation: 0,
        proposedSnapshotJson: buildShareholderSnapshot(data, keptCert, keptName),
      })
      toaster.success({ description: 'Cambio de accionista guardado en la solicitud' })
      await load()
      return
    }
    await modificationRequestService.upsertDraftChange(mrId, {
      entityName: 'SupplierShareholder', entityId: shId, operation: 1,
      proposedSnapshotJson: buildShareholderSnapshot(data, cert, file?.name ?? null),
    })
    toaster.success({ description: 'Cambio de accionista guardado en la solicitud' })
    await load()
  }

  const stageShareholderRemove = async (shId: string) => {
    if (!mrId) return
    if (shId.startsWith('staged-')) {
      await modificationRequestService.removeDraftChange(mrId, shId.substring('staged-'.length))
    } else {
      await modificationRequestService.upsertDraftChange(mrId, {
        entityName: 'SupplierShareholder', entityId: shId, operation: 2,
        proposedSnapshotJson: JSON.stringify({ id: shId }),
      })
    }
    toaster.success({ description: 'Eliminación guardada en la solicitud' })
    await load()
  }

  // ── IGAFOM staging ────────────────────────────────────────────────────────
  const stageIgafomAdd = async (miningRightId: string, data: AddIgafomRecordRequest, receiptFile?: File, documentFile?: File, previous: Record<string, unknown> | null = null) => {
    if (!mrId) return
    const [newDocumentId, newReceiptId] = await Promise.all([
      documentFile ? modificationRequestService.uploadStagedFile(mrId, documentFile).then(r => r.fileAttachmentId) : Promise.resolve(null),
      receiptFile ? modificationRequestService.uploadStagedFile(mrId, receiptFile).then(r => r.fileAttachmentId) : Promise.resolve(null),
    ])
    const documentFileAttachmentId = keepStagedValue(newDocumentId, previous, 'documentFileAttachmentId')
    const receiptFileAttachmentId = keepStagedValue(newReceiptId, previous, 'receiptFileAttachmentId')
    await modificationRequestService.upsertDraftChange(mrId, {
      entityName: 'IgafomRecord', entityId: null, operation: 0,
      proposedSnapshotJson: JSON.stringify({
        miningRightId,
        presentationDate: data.presentationDate ?? null,
        expirationDate: data.expirationDate ?? null,
        fileNumber: data.fileNumber ?? null,
        type: data.type ?? null,
        exploitationMethod: data.exploitationMethod ?? null,
        documentStatus: data.documentStatus ?? null,
        substanceId: data.substanceId ?? null,
        productiveCapacity: data.productiveCapacity ?? null,
        documentFileAttachmentId, receiptFileAttachmentId,
      }),
    })
    toaster.success({ description: 'IGAFOM agregado al staging' })
    await load()
  }

  const stageIgafomUpdate = async (miningRightId: string, igId: string, data: AddIgafomRecordRequest, receiptFile?: File, documentFile?: File) => {
    if (!mrId) return
    if (igId.startsWith('staged-')) {
      const changeId = igId.substring('staged-'.length)
      const previous = stagedPayload(changes, igId)
      await modificationRequestService.removeDraftChange(mrId, changeId)
      await stageIgafomAdd(miningRightId, data, receiptFile, documentFile, previous)
      return
    }
    const [documentFileAttachmentId, receiptFileAttachmentId] = await Promise.all([
      documentFile ? modificationRequestService.uploadStagedFile(mrId, documentFile).then(r => r.fileAttachmentId) : Promise.resolve(null),
      receiptFile ? modificationRequestService.uploadStagedFile(mrId, receiptFile).then(r => r.fileAttachmentId) : Promise.resolve(null),
    ])
    await modificationRequestService.upsertDraftChange(mrId, {
      entityName: 'IgafomRecord', entityId: igId, operation: 1,
      proposedSnapshotJson: JSON.stringify({
        miningRightId,
        presentationDate: data.presentationDate ?? null,
        expirationDate: data.expirationDate ?? null,
        fileNumber: data.fileNumber ?? null,
        type: data.type ?? null,
        exploitationMethod: data.exploitationMethod ?? null,
        documentStatus: data.documentStatus ?? null,
        substanceId: data.substanceId ?? null,
        productiveCapacity: data.productiveCapacity ?? null,
        documentFileAttachmentId, receiptFileAttachmentId,
      }),
    })
    toaster.success({ description: 'Cambio de IGAFOM guardado en la solicitud' })
    await load()
  }

  const stageIgafomRemove = async (_miningRightId: string, igId: string) => {
    if (!mrId) return
    if (igId.startsWith('staged-')) {
      await modificationRequestService.removeDraftChange(mrId, igId.substring('staged-'.length))
    } else {
      await modificationRequestService.upsertDraftChange(mrId, {
        entityName: 'IgafomRecord', entityId: igId, operation: 2,
        proposedSnapshotJson: JSON.stringify({ id: igId }),
      })
    }
    toaster.success({ description: 'Eliminación guardada en la solicitud' })
    await load()
  }

  // ── LegalDocumentRecord staging (espejo de IGAFOM, observación 13) ────────
  const stageLegalDocAdd = async (miningRightId: string, data: AddLegalDocumentRecordRequest, receiptFile?: File, documentFile?: File, previous: Record<string, unknown> | null = null) => {
    if (!mrId) return
    const [newDocumentId, newReceiptId] = await Promise.all([
      documentFile ? modificationRequestService.uploadStagedFile(mrId, documentFile).then(r => r.fileAttachmentId) : Promise.resolve(null),
      receiptFile ? modificationRequestService.uploadStagedFile(mrId, receiptFile).then(r => r.fileAttachmentId) : Promise.resolve(null),
    ])
    const documentFileAttachmentId = keepStagedValue(newDocumentId, previous, 'documentFileAttachmentId')
    const receiptFileAttachmentId = keepStagedValue(newReceiptId, previous, 'receiptFileAttachmentId')
    await modificationRequestService.upsertDraftChange(mrId, {
      entityName: 'LegalDocumentRecord', entityId: null, operation: 0,
      proposedSnapshotJson: JSON.stringify({
        miningRightId,
        documentType: data.documentType ?? null,
        presentationDate: data.presentationDate ?? null,
        expirationDate: data.expirationDate ?? null,
        fileNumber: data.fileNumber ?? null,
        documentStatus: data.documentStatus ?? null,
        documentFileAttachmentId, receiptFileAttachmentId,
      }),
    })
    toaster.success({ description: 'Documento legal agregado al staging' })
    await load()
  }

  const stageLegalDocUpdate = async (miningRightId: string, ldId: string, data: AddLegalDocumentRecordRequest, receiptFile?: File, documentFile?: File) => {
    if (!mrId) return
    if (ldId.startsWith('staged-')) {
      const changeId = ldId.substring('staged-'.length)
      const previous = stagedPayload(changes, ldId)
      await modificationRequestService.removeDraftChange(mrId, changeId)
      await stageLegalDocAdd(miningRightId, data, receiptFile, documentFile, previous)
      return
    }
    const [documentFileAttachmentId, receiptFileAttachmentId] = await Promise.all([
      documentFile ? modificationRequestService.uploadStagedFile(mrId, documentFile).then(r => r.fileAttachmentId) : Promise.resolve(null),
      receiptFile ? modificationRequestService.uploadStagedFile(mrId, receiptFile).then(r => r.fileAttachmentId) : Promise.resolve(null),
    ])
    await modificationRequestService.upsertDraftChange(mrId, {
      entityName: 'LegalDocumentRecord', entityId: ldId, operation: 1,
      proposedSnapshotJson: JSON.stringify({
        miningRightId,
        documentType: data.documentType ?? null,
        presentationDate: data.presentationDate ?? null,
        expirationDate: data.expirationDate ?? null,
        fileNumber: data.fileNumber ?? null,
        documentStatus: data.documentStatus ?? null,
        documentFileAttachmentId, receiptFileAttachmentId,
      }),
    })
    toaster.success({ description: 'Cambio de documento legal guardado en la solicitud' })
    await load()
  }

  const stageLegalDocRemove = async (_miningRightId: string, ldId: string) => {
    if (!mrId) return
    if (ldId.startsWith('staged-')) {
      await modificationRequestService.removeDraftChange(mrId, ldId.substring('staged-'.length))
    } else {
      await modificationRequestService.upsertDraftChange(mrId, {
        entityName: 'LegalDocumentRecord', entityId: ldId, operation: 2,
        proposedSnapshotJson: JSON.stringify({ id: ldId }),
      })
    }
    toaster.success({ description: 'Eliminación guardada en la solicitud' })
    await load()
  }

  // ── MiningRight (polygon) + Components staging ────────────────────────────
  const stagePolygonSave = async (dmId: string, vertices: { northing: number; easting: number }[], utmZone: number) => {
    if (!mrId) return
    // Convert UTM vertices to WGS84 lat/lng before building the WKT — the backend stores
    // MiningRight.Boundary as EPSG:4326. If we shipped raw UTM numbers labeled as WGS84,
    // the polygon would be corrupted in the DB on approval.
    // WKT convention: coord order is "x y" = "longitude latitude" for EPSG:4326.
    const latLngs = vertices.map(v => utmToLatLng(v.northing, v.easting, utmZone))
    const closed = latLngs.length > 0 && (
      latLngs[0]!.lat !== latLngs[latLngs.length - 1]!.lat ||
      latLngs[0]!.lng !== latLngs[latLngs.length - 1]!.lng
    )
      ? [...latLngs, latLngs[0]!]
      : latLngs
    const coords = closed.map(p => `${p.lng.toFixed(6)} ${p.lat.toFixed(6)}`).join(', ')
    const wgs84Wkt = `POLYGON((${coords}))`

    // Mergear con el cambio existente del DM (p. ej. un toggle de explosivos) para no pisarlo.
    const existing = changes.find(c => c.entityName === 'MiningRight' && c.entityId === dmId && c.operation === 1)
    let base: Record<string, unknown> = {}
    if (existing) { try { base = JSON.parse(existing.proposedSnapshotJson) } catch { /* */ } }
    await modificationRequestService.upsertDraftChange(mrId, {
      entityName: 'MiningRight', entityId: dmId, operation: 1,
      proposedSnapshotJson: JSON.stringify({ ...base, boundaryWkt: wgs84Wkt, utmZone }),
    })
    toaster.success({ description: 'Polígono guardado en la solicitud: se aplica al aprobarla' })
    await load()
  }

  const stageComponentAdd = async (dmId: string, data: { componentTypeId: string; name?: string; northing: number; easting: number }) => {
    if (!mrId) return {}
    // Component coords come from the UI as UTM (Northing/Easting). The backend stores them
    // as EPSG:4326 Point. Derive the UTM zone from the mining right's ubigeoCode and convert.
    // Con lo stageado encima: si esta misma solicitud cambia el polígono/zona UTM, el punto
    // debe convertirse con la zona NUEVA (con la vieja quedaba ~650 km desplazado).
    const mr = overlaidMiningRights.find(m => m.id === dmId)
    // Zona persistida del DM (fuente de verdad); fallback a la geometría/ubigeo
    // para no descuadrar el componente respecto al polígono.
    const utmZone = mr?.utmZone ?? getUtmZoneFromGeoJson(mr?.geoJson, mr?.ubigeoCode)
    const { lat, lng } = utmToLatLng(data.northing, data.easting, utmZone)
    // Contra el polígono vigente en la solicitud (el propuesto, si esta misma solicitud lo
    // cambia). Antes se guardaba false fijo y el editor nunca avisaba "fuera del polígono".
    const isOutsidePolygon = isPointInGeoJson(lat, lng, mr?.geoJson) === false
    await modificationRequestService.upsertDraftChange(mrId, {
      entityName: 'MiningRightComponent', entityId: null, operation: 0,
      proposedSnapshotJson: JSON.stringify({
        miningRightId: dmId,
        componentTypeId: data.componentTypeId,
        componentTypeName: componentTypeNames[data.componentTypeId] ?? null,
        name: data.name ?? null,
        // El backend persiste lat/lng (EPSG:4326); el UTM original se guarda también
        // para que el overlay y el diff muestren N/E como los digitó el usuario.
        latitude: lat,
        longitude: lng,
        northing: data.northing,
        easting: data.easting,
        isOutsidePolygon,
      }),
    })
    toaster.success({ description: 'Componente agregado al staging' })
    await load()
    return { isOutsidePolygon }
  }

  const stageComponentRemove = async (_dmId: string, componentId: string) => {
    if (!mrId) return
    if (componentId.startsWith('staged-')) {
      await modificationRequestService.removeDraftChange(mrId, componentId.substring('staged-'.length))
    } else {
      await modificationRequestService.upsertDraftChange(mrId, {
        entityName: 'MiningRightComponent', entityId: componentId, operation: 2,
        proposedSnapshotJson: JSON.stringify({ id: componentId }),
      })
    }
    toaster.success({ description: 'Eliminación de componente guardada en la solicitud' })
    await load()
  }

  // ── ProductionDeclaration staging ─────────────────────────────────────────
  const stageProductionAdd = async (miningRightId: string, data: AddProductionDeclarationRequest, documentFile?: File) => {
    if (!mrId) return
    const documentFileAttachmentId = documentFile
      ? (await modificationRequestService.uploadStagedFile(mrId, documentFile)).fileAttachmentId
      : null
    await modificationRequestService.upsertDraftChange(mrId, {
      entityName: 'ProductionDeclaration', entityId: null, operation: 0,
      proposedSnapshotJson: JSON.stringify({ miningRightId, ...data, documentFileAttachmentId }),
    })
    toaster.success({ description: 'Declaración de producción guardada en la solicitud' })
    await load()
  }

  const stageProductionRemove = async (_miningRightId: string, declId: string) => {
    if (!mrId) return
    if (declId.startsWith('staged-')) {
      await modificationRequestService.removeDraftChange(mrId, declId.substring('staged-'.length))
    } else {
      await modificationRequestService.upsertDraftChange(mrId, {
        entityName: 'ProductionDeclaration', entityId: declId, operation: 2,
        proposedSnapshotJson: JSON.stringify({ id: declId }),
      })
    }
    toaster.success({ description: 'Eliminación de declaración guardada en la solicitud' })
    await load()
  }

  // ── TaxReport staging (insert-only; cada modificación agrega una fila al historial) ─
  const stageTaxReportAdd = async (data: AddTaxReportRequest, documentFile: File) => {
    if (!mrId || !proveedor?.companyBusinessId) return
    const { fileAttachmentId } = await modificationRequestService.uploadStagedFile(mrId, documentFile)
    await modificationRequestService.upsertDraftChange(mrId, {
      entityName: 'TaxReport', entityId: null, operation: 0,
      proposedSnapshotJson: JSON.stringify({
        companyBusinessId: proveedor.companyBusinessId,
        code: data.code ?? null,
        issueDate: data.issueDate,
        documentFileAttachmentId: fileAttachmentId,
      }),
    })
    toaster.success({ description: 'Reporte tributario guardado en la solicitud' })
    await load()
  }

  const stageTaxReportRemove = async (reportId: string) => {
    if (!mrId) return
    if (reportId.startsWith('staged-')) {
      await modificationRequestService.removeDraftChange(mrId, reportId.substring('staged-'.length))
      toaster.success({ description: 'Reporte tributario removido del staging' })
      await load()
    }
  }

  // ── PowerValidity staging ───────────────────────────────────────────────
  const stagePowerValidityAdd = async (cpId: string, data: AddPowerValidityRequest, documentFile: File) => {
    if (!mrId || !proveedor?.companyBusinessId) return
    // Representante legal agregado en ESTA misma solicitud: aún no existe en la base, así
    // que la vigencia referencia el cambio que lo crea y el aplicador la liga al aprobar.
    // Un id "pending-…" (alta optimista sin recargar) todavía no tiene cambio: se pide esperar.
    if (cpId.startsWith('pending-')) {
      toaster.error({ description: 'Espera a que el representante legal termine de agregarse e intenta de nuevo.' })
      return
    }
    const stagedPersonChangeId = cpId.startsWith('staged-') ? cpId.substring('staged-'.length) : null
    const { fileAttachmentId } = await modificationRequestService.uploadStagedFile(mrId, documentFile)
    await modificationRequestService.upsertDraftChange(mrId, {
      entityName: 'PowerValidity', entityId: null, operation: 0,
      proposedSnapshotJson: JSON.stringify({
        companyBusinessId: proveedor.companyBusinessId,
        companyPersonId: stagedPersonChangeId ? null : cpId,
        companyPersonDraftChangeId: stagedPersonChangeId,
        code: data.code,
        issueDate: data.issueDate,
        documentFileAttachmentId: fileAttachmentId,
      }),
    })
    toaster.success({ description: 'Vigencia de poder guardada en la solicitud' })
    await load()
  }

  const stagePowerValidityRemove = async (powerValidityId: string) => {
    if (!mrId) return
    if (powerValidityId.startsWith('staged-')) {
      await modificationRequestService.removeDraftChange(mrId, powerValidityId.substring('staged-'.length))
      toaster.success({ description: 'Vigencia de poder removida del staging' })
      await load()
    }
  }

  // ── IQBF / Explosivos por Derecho Minero staging ──────────────────────────
  // El paso 7 maneja autorizaciones SUCAMEC + manipuladores por D.M.; el flag
  // `usesExplosives` vive en el MiningRight, así que se mergea en el cambio del DM.
  const stageUsesExplosivesToggle = async (dmId: string, value: boolean) => {
    if (!mrId) return
    const existing = changes.find(c => c.entityName === 'MiningRight' && c.entityId === dmId && c.operation === 1)
    let base: Record<string, unknown> = {}
    if (existing) { try { base = JSON.parse(existing.proposedSnapshotJson) } catch { /* */ } }
    await modificationRequestService.upsertDraftChange(mrId, {
      entityName: 'MiningRight', entityId: dmId, operation: 1,
      proposedSnapshotJson: JSON.stringify({ ...base, usesExplosives: value }),
    })
    toaster.success({ description: 'Cambio de uso de explosivos guardado en la solicitud' })
    await load()
  }

  const stageAuthorizationAdd = async (dmId: string, data: AddExplosiveAuthorizationRequest, documentFile?: File, previous: Record<string, unknown> | null = null) => {
    if (!mrId) return
    const newDocumentId = documentFile
      ? (await modificationRequestService.uploadStagedFile(mrId, documentFile)).fileAttachmentId
      : null
    const documentFileAttachmentId = keepStagedValue(newDocumentId, previous, 'documentFileAttachmentId')
    const documentFileName = newDocumentId ? documentFile?.name ?? null : keepStagedValue<string>(null, previous, 'documentFileName')
    await modificationRequestService.upsertDraftChange(mrId, {
      entityName: 'ExplosiveAuthorization', entityId: null, operation: 0,
      proposedSnapshotJson: JSON.stringify({ miningRightId: dmId, ...data, documentFileAttachmentId, documentFileName }),
    })
    toaster.success({ description: 'Autorización de explosivos guardada en la solicitud' })
    await load()
  }

  const stageAuthorizationUpdate = async (dmId: string, authId: string, data: AddExplosiveAuthorizationRequest, documentFile?: File) => {
    if (!mrId) return
    if (authId.startsWith('staged-')) {
      const previous = stagedPayload(changes, authId)
      await modificationRequestService.removeDraftChange(mrId, authId.substring('staged-'.length))
      await stageAuthorizationAdd(dmId, data, documentFile, previous)
      return
    }
    const documentFileAttachmentId = documentFile
      ? (await modificationRequestService.uploadStagedFile(mrId, documentFile)).fileAttachmentId
      : null
    await modificationRequestService.upsertDraftChange(mrId, {
      entityName: 'ExplosiveAuthorization', entityId: authId, operation: 1,
      proposedSnapshotJson: JSON.stringify({ miningRightId: dmId, ...data, documentFileAttachmentId, documentFileName: documentFile?.name ?? null }),
    })
    toaster.success({ description: 'Cambio de autorización guardado en la solicitud' })
    await load()
  }

  const stageAuthorizationRemove = async (_dmId: string, authId: string) => {
    if (!mrId) return
    if (authId.startsWith('staged-')) {
      await modificationRequestService.removeDraftChange(mrId, authId.substring('staged-'.length))
    } else {
      await modificationRequestService.upsertDraftChange(mrId, {
        entityName: 'ExplosiveAuthorization', entityId: authId, operation: 2,
        proposedSnapshotJson: JSON.stringify({ id: authId }),
      })
    }
    toaster.success({ description: 'Eliminación de autorización guardada en la solicitud' })
    await load()
  }

  const stageHandlerAdd = async (dmId: string, data: AddExplosiveHandlerRequest) => {
    if (!mrId) return
    await modificationRequestService.upsertDraftChange(mrId, {
      entityName: 'ExplosiveHandler', entityId: null, operation: 0,
      proposedSnapshotJson: JSON.stringify({ miningRightId: dmId, ...data }),
    })
    toaster.success({ description: 'Manipulador guardado en la solicitud' })
    await load()
  }

  const stageBulkHandlers = async (dmId: string, items: AddExplosiveHandlerRequest[]) => {
    if (!mrId || items.length === 0) return
    // Secuencial: cada manipulador es un INSERT independiente (entityId null).
    for (const item of items) {
      await modificationRequestService.upsertDraftChange(mrId, {
        entityName: 'ExplosiveHandler', entityId: null, operation: 0,
        proposedSnapshotJson: JSON.stringify({ miningRightId: dmId, ...item }),
      })
    }
    toaster.success({ description: `${items.length} manipuladores stageados` })
    await load()
  }

  const stageHandlerRemove = async (_dmId: string, handlerId: string) => {
    if (!mrId) return
    if (handlerId.startsWith('staged-')) {
      await modificationRequestService.removeDraftChange(mrId, handlerId.substring('staged-'.length))
    } else {
      await modificationRequestService.upsertDraftChange(mrId, {
        entityName: 'ExplosiveHandler', entityId: handlerId, operation: 2,
        proposedSnapshotJson: JSON.stringify({ id: handlerId }),
      })
    }
    toaster.success({ description: 'Eliminación de manipulador guardada en la solicitud' })
    await load()
  }

  // ── Acreditación staging (form-based) ─────────────────────────────────────
  const stageAcreditacionChanges = async () => {
    if (!mrId || !proveedor?.miningSupplier?.id) return
    if (!activeStepRef.current) return
    const isValid = await activeStepRef.current.trigger()
    if (!isValid) return
    const values = activeStepRef.current.getValues() as {
      categoryId: number
    }
    await modificationRequestService.upsertDraftChange(mrId, {
      entityName: 'MiningSupplier',
      entityId: proveedor.miningSupplier.id,
      operation: 1,
      // Merge: el mismo cambio MiningSupplier lleva renta y SPLAFT; antes se reemplazaba
      // entero y lo stageado en Finanzas se perdía al guardar Acreditación.
      proposedSnapshotJson: JSON.stringify({
        ...extractCurrentMiningSupplierStaging(changes),
        categoryId: values.categoryId,
      }),
    })
    toaster.success({ description: 'Cambios de acreditación guardados en la solicitud' })
    await load()
  }

  // ── Identidad: datos de contacto de la empresa (form-based) ───────────────
  // Antes estos campos eran editables en la solicitud pero no había stager ni botón:
  // lo escrito se perdía en silencio. Sólo se stagean los campos que cambian; se conservan
  // los datos SUNAT que haya stageado "Actualizar datos RUC" en el mismo cambio Company.
  const stageCompanyChanges = async () => {
    if (!mrId || !proveedor) return
    if (!activeStepRef.current) return
    const isValid = await activeStepRef.current.trigger()
    if (!isValid) return
    const values = activeStepRef.current.getValues() as Record<string, string | null | undefined>
    const editable = ['commercialName', 'taxAddress', 'ubigeoCode', 'mainPhone', 'mainEmail', 'website'] as const
    const existing = changes.find(ch => ch.entityName === 'Company' && ch.operation === 1)
    const next: Record<string, unknown> = existing ? JSON.parse(existing.proposedSnapshotJson) : {}
    for (const field of editable) {
      const proposed = (values[field] ?? '').trim()
      const current = ((proveedor as unknown as Record<string, string | null | undefined>)[field] ?? '').trim()
      if (proposed !== current) next[field] = proposed
      else delete next[field]
    }
    if (Object.keys(next).length === 0) {
      if (existing) await modificationRequestService.removeDraftChange(mrId, existing.id)
      toaster.success({ description: 'Sin cambios en los datos de la empresa' })
    } else {
      await modificationRequestService.upsertDraftChange(mrId, {
        entityName: 'Company', entityId: proveedor.id, operation: 1,
        proposedSnapshotJson: JSON.stringify(next),
      })
      toaster.success({ description: 'Cambios de identidad guardados en la solicitud' })
    }
    await load()
  }

  // ── Contrato staging (insert-only; reemplaza el contrato activo) ──────────
  const stageContractUpload = async (file: File, contractDate?: string, notes?: string, expirationDate?: string) => {
    if (!mrId || !proveedor?.companyBusinessId) return
    // Solo tiene sentido un contrato stageado; si ya hay uno, lo reemplazamos.
    const existing = changes.find(c => c.entityName === 'SupplierContract' && c.operation === 0)
    if (existing) await modificationRequestService.removeDraftChange(mrId, existing.id)
    const { fileAttachmentId } = await modificationRequestService.uploadStagedFile(mrId, file)
    await modificationRequestService.upsertDraftChange(mrId, {
      entityName: 'SupplierContract', entityId: null, operation: 0,
      proposedSnapshotJson: JSON.stringify({
        companyBusinessId: proveedor.companyBusinessId,
        contractDate: contractDate ?? null,
        expirationDate: expirationDate ?? null,
        notes: notes ?? null,
        documentFileAttachmentId: fileAttachmentId,
        documentFileName: file.name,
      }),
    })
    toaster.success({ description: 'Contrato guardado en la solicitud' })
    await load()
  }

  // ── Finanzas staging ──────────────────────────────────────────────────────
  const stagePersonAdd = async (_companyId: string, data: AddCompanyPersonRequest): Promise<CompanyPersonResponse> => {
    if (!mrId || !proveedor) throw new Error('MR no cargada')
    // Persona ya registrada (elegida en la búsqueda): sólo llega personId + tipo. Antes se
    // stageaba sin documento ni nombre y la aprobación de TODA la solicitud fallaba con
    // "identityDoc requerido". Se envía el personId y, para el diff, sus datos visibles.
    if (data.personId) {
      const p = await personsService.getById(data.personId)
      data = {
        ...data,
        documentType: p.documentType, identityDoc: p.identityDoc, names: p.names,
        paternalSurname: p.paternalSurname, maternalSurname: p.maternalSurname ?? undefined,
        email: p.email ?? undefined, phoneMobile: p.phoneMobile ?? undefined,
      }
    }
    await modificationRequestService.upsertDraftChange(mrId, {
      entityName: 'CompanyPerson', entityId: null, operation: 0,
      proposedSnapshotJson: JSON.stringify({
        companyId: proveedor.id,
        personId: data.personId ?? null,
        documentType: data.documentType ?? null,
        identityDoc: data.identityDoc ?? null,
        names: data.names ?? null,
        paternalSurname: data.paternalSurname ?? null,
        maternalSurname: data.maternalSurname ?? null,
        gender: data.gender ?? null,
        email: data.email ?? null,
        phoneMobile: data.phoneMobile ?? null,
        contactType: data.contactType,
      }),
    })
    toaster.success({ description: 'Contacto agregado al staging' })
    await load()
    const fullName = [data.names, data.paternalSurname, data.maternalSurname].filter(Boolean).join(' ')
    return {
      id: `staged-${Date.now()}`,
      personId: `staged-${Date.now()}`,
      fullName,
      identityDoc: data.identityDoc ?? null,
      contactType: data.contactType,
      contactTypeName: null,
      email: data.email ?? null,
      phone: data.phoneMobile ?? null,
    }
  }

  const stagePersonRemove = async (_companyId: string, companyPersonId: string) => {
    if (!mrId) return
    let dependents: DraftChangeResponse[]
    if (companyPersonId.startsWith('staged-')) {
      const change = findStagedInsert(changes, 'CompanyPerson', companyPersonId)
      dependents = change ? dependentPowerValidities(changes, { draftChangeId: change.id }) : []
      if (change) await modificationRequestService.removeDraftChange(mrId, change.id)
    } else {
      dependents = dependentPowerValidities(changes, { companyPersonId })
      await modificationRequestService.upsertDraftChange(mrId, {
        entityName: 'CompanyPerson', entityId: companyPersonId, operation: 2,
        proposedSnapshotJson: JSON.stringify({ id: companyPersonId }),
      })
    }
    // Su vigencia de poder stageada se va con él (ver dependentPowerValidities).
    for (const pv of dependents) await modificationRequestService.removeDraftChange(mrId, pv.id)
    toaster.success({ description: dependents.length > 0
      ? 'Eliminación de contacto guardada en la solicitud (se quitó también su vigencia de poder)'
      : 'Eliminación de contacto guardada en la solicitud' })
    await load()
  }

  const stageIncomeTaxChange = async (value: number) => {
    if (!mrId || !proveedor?.miningSupplier?.id) return
    await modificationRequestService.upsertDraftChange(mrId, {
      entityName: 'MiningSupplier',
      entityId: proveedor.miningSupplier.id,
      operation: 1,
      proposedSnapshotJson: JSON.stringify({
        ...extractCurrentMiningSupplierStaging(changes),
        incomeTaxCategoryId: value,
      }),
    })
    toaster.success({ description: 'Cambio de categoría de renta guardado en la solicitud' })
    await load()
  }

  // ── SPLAFT staging (declaración + documento, merge en el cambio de MiningSupplier) ─
  const stageSplaftDeclared = async (value: boolean) => {
    if (!mrId || !proveedor?.miningSupplier?.id) return
    await modificationRequestService.upsertDraftChange(mrId, {
      entityName: 'MiningSupplier', entityId: proveedor.miningSupplier.id, operation: 1,
      proposedSnapshotJson: JSON.stringify({ ...extractCurrentMiningSupplierStaging(changes), splaftDeclared: value }),
    })
    toaster.success({ description: 'Declaración SPLAFT guardada en la solicitud' })
    await load()
  }

  const stageSplaftFile = async (file: File | null) => {
    if (!mrId || !proveedor?.miningSupplier?.id || !file) return
    const { fileAttachmentId } = await modificationRequestService.uploadStagedFile(mrId, file)
    await modificationRequestService.upsertDraftChange(mrId, {
      entityName: 'MiningSupplier', entityId: proveedor.miningSupplier.id, operation: 1,
      proposedSnapshotJson: JSON.stringify({
        ...extractCurrentMiningSupplierStaging(changes),
        splaftDeclared: true,
        splaftDocumentFileAttachmentId: fileAttachmentId,
        splaftDocumentFileName: file.name,
      }),
    })
    toaster.success({ description: 'Documento SPLAFT guardado en la solicitud' })
    await load()
  }

  const stageBankAdd = async (data: { accountNumber: string; bankEntityId: number; accountTypeId: number }): Promise<BankAccountResponse> => {
    if (!mrId || !proveedor) throw new Error('MR no cargada')
    await modificationRequestService.upsertDraftChange(mrId, {
      entityName: 'BankAccount', entityId: null, operation: 0,
      proposedSnapshotJson: JSON.stringify({
        companyId: proveedor.id, accountNumber: data.accountNumber,
        bankEntityId: data.bankEntityId, accountTypeId: data.accountTypeId,
      }),
    })
    toaster.success({ description: 'Cuenta bancaria agregada al staging' })
    await load()
    return {
      id: `staged-${Date.now()}`,
      accountNumber: data.accountNumber,
      bankEntityId: data.bankEntityId,
      bankEntityName: enumLabel(BANK_ENTITIES, data.bankEntityId),
      accountTypeId: data.accountTypeId,
      accountTypeName: enumLabel(ACCOUNT_TYPES, data.accountTypeId),
    } as BankAccountResponse
  }

  const stageBankRemove = async (accountId: string) => {
    if (!mrId) return
    if (accountId.startsWith('staged-')) {
      const change = findStagedInsert(changes, 'BankAccount', accountId)
      if (change) await modificationRequestService.removeDraftChange(mrId, change.id)
    } else {
      await modificationRequestService.upsertDraftChange(mrId, {
        entityName: 'BankAccount', entityId: accountId, operation: 2,
        proposedSnapshotJson: JSON.stringify({ id: accountId }),
      })
    }
    toaster.success({ description: 'Eliminación guardada en la solicitud' })
    await load()
  }

  // ── MR lifecycle ──────────────────────────────────────────────────────────
  const runCancel = async () => {
    if (!mrId) return
    setBusy(true)
    try {
      await modificationRequestService.cancel(mrId)
      toaster.success({ description: 'Solicitud anulada' })
      if (proveedorId) navigate(`/app/proveedores/${proveedorId}/modificaciones`)
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'Error al anular' })
    } finally { setBusy(false); setConfirming(null) }
  }
  const runApprove = async () => {
    if (!mrId) return
    setBusy(true)
    try {
      await modificationRequestService.approve(mrId, reviewNotes.trim() || undefined)
      toaster.success({ description: 'Solicitud aprobada. Cambios aplicados al expediente.' })
      if (proveedorId) navigate(`/app/proveedores/${proveedorId}/modificaciones`)
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'Error al aprobar' })
    } finally { setBusy(false); setConfirming(null) }
  }
  const runReject = async () => {
    if (!mrId) return
    if (!reviewNotes.trim()) {
      toaster.error({ description: 'Indica el motivo del rechazo para que el solicitante pueda corregirlo.' })
      return
    }
    setBusy(true)
    try {
      await modificationRequestService.reject(mrId, reviewNotes.trim())
      toaster.success({ description: 'Solicitud rechazada' })
      if (proveedorId) navigate(`/app/proveedores/${proveedorId}/modificaciones`)
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'Error al rechazar' })
    } finally { setBusy(false); setConfirming(null) }
  }
  const handleRefreshExternal = async () => {
    if (!mrId) return
    setRefreshing(true)
    try {
      const r = await modificationRequestService.refreshExternalData(mrId)
      await load()
      setRefreshResult(r.totalChanges > 0 ? 'diffs' : 'no-diffs')
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'Error al actualizar datos RUC' })
    } finally {
      setRefreshing(false)
    }
  }

  const handleRefreshDms = async () => {
    if (!mrId) return
    setRefreshingDms(true)
    try {
      const r = await modificationRequestService.refreshMiningRightsData(mrId)
      await load()
      setRefreshDmsResult(r.totalChanges > 0 ? 'diffs' : 'no-diffs')
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'Error al actualizar DMs' })
    } finally {
      setRefreshingDms(false)
    }
  }

  const handleSubmit = async () => {
    if (!mrId) return
    setBusy(true)
    try {
      await modificationRequestService.submit(mrId)
      toaster.success({ description: 'Solicitud enviada a revisión' })
      await load()
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'Error al enviar' })
    } finally { setBusy(false) }
  }

  // ── Tabs ──────────────────────────────────────────────────────────────────
  const tabs = [
    { key: 'identidad' as const, label: 'Identidad', icon: <Fingerprint size={18} />, hasError: false },
    { key: 'acreditacion' as const, label: 'Acreditación', icon: <ShieldCheck size={18} />, hasError: false },
    { key: 'contrato' as const, label: 'Contrato', icon: <Signature size={18} />, hasError: false },
    { key: 'derechos' as const, label: 'Derechos mineros', icon: <Mountain size={18} />, hasError: false },
    { key: 'igafom' as const, label: 'IGAFOM', icon: <FileText size={18} />, hasError: false },
    { key: 'legaldocs' as const, label: 'Otros Documentos Legales', icon: <FileText size={18} />, hasError: false },
    { key: 'produccion' as const, label: 'Producción', icon: <Factory size={18} />, hasError: false },
    { key: 'iqbf' as const, label: 'IQBF', icon: <AlertTriangle size={18} />, hasError: false },
    { key: 'finanzas' as const, label: 'Finanzas', icon: <Landmark size={18} />, hasError: false },
    ...(isCorporation
      ? [{ key: 'accionistas' as const, label: 'Accionistas (UBO)', icon: <Users size={18} />, hasError: false }]
      : []),
  ]

  const STEP_TITLES: Record<TabType, { title: string; description: string }> = {
    identidad: { title: 'Identidad', description: 'Datos de la empresa y contactos autorizados. Agrega o remueve personas asociadas al proveedor.' },
    acreditacion: { title: 'Acreditación', description: 'Categoría minera y capacidad productiva. Los cambios se aplican al aprobar la solicitud.' },
    contrato: { title: 'Contrato', description: 'Reemplaza el contrato vigente del proveedor. El anterior pasa al historial al aprobar la solicitud.' },
    derechos: { title: 'Derechos mineros', description: 'Ajusta polígonos y componentes de los derechos mineros del proveedor.' },
    igafom: { title: 'Registro IGAFOM', description: 'Agrega, edita o remueve registros IGAFOM por derecho minero.' },
    legaldocs: { title: 'Otros Documentos Legales', description: 'Documentos legales complementarios por derecho minero (COM, DIA, EIA, ITS, PAMA, PCM, etc.).' },
    produccion: { title: 'Declaraciones de producción', description: 'Agrega o remueve declaraciones anuales de producción (DPS) por derecho minero.' },
    iqbf: { title: 'Registro IQBF', description: 'Explosivos, OTS y COM. Adjunta nuevos documentos si aplica.' },
    finanzas: { title: 'Finanzas', description: 'Cuentas bancarias y datos tributarios.' },
    accionistas: { title: 'Accionistas y Beneficiarios Finales (UBO)', description: 'Agrega, edita o remueve accionistas de la sociedad. El Certificado Único Laboral se valida automáticamente.' },
  }

  if (loading) return <Box p={8} textAlign="center"><Text color={c.textMuted}>Cargando solicitud...</Text></Box>
  if (!mr || !proveedor) return <Box p={8} textAlign="center"><Text color={c.text}>Solicitud no encontrada</Text></Box>

  const currentStep = STEP_TITLES[activeTab]
  const activeIndex = TAB_ORDER.indexOf(activeTab)
  const isFormTab = activeTab === 'acreditacion' || activeTab === 'identidad'

  return (
    <ModificationModeProvider value={true}>
    <Flex mx={-8} mt={-4}>
      {!reviewMode && (
        <WizardSidebar
          title="Modificar minero"
          subtitle={`Paso ${activeIndex + 1} de ${TAB_ORDER.length}`}
          steps={tabs}
          activeStep={activeTab}
          completedSteps={new Set()}
          onStepClick={(t) => setActiveTab(t as TabType)}
        />
      )}

      <Box flex={1} py={4} px={6} bg="white" display="flex" flexDirection="column" minH="calc(100vh - 2rem)"
        maxW={reviewMode ? '1100px' : undefined} mx={reviewMode ? 'auto' : undefined}>
        <Box flex={1} display="flex" flexDirection="column">
          <Box mb={6}>
            <Flex gap={3} alignItems="center" mb={2} flexWrap="wrap" justifyContent="space-between">
              <Flex gap={3} alignItems="center" flexWrap="wrap">
                <Text fontSize="sm" fontWeight="700" color={c.text}>{proveedor.legalName}</Text>
                <Text fontSize="xs" color={c.textSecondary} fontFamily="monospace" bg="gray.100" px={2} py={0.5} borderRadius="md">
                  RUC {proveedor.ruc}
                </Text>
                <Text fontSize="xs" color={c.textSecondary} fontFamily="monospace" bg={`${brand.green}15`} px={2} py={0.5} borderRadius="md">
                  {mr.code}
                </Text>
                <StatusBadge status={mr.requestStatus} />
              </Flex>
              {isDraft && (
                <Box as="button" type="button"
                  display="inline-flex" alignItems="center" gap={2}
                  px={3} py={1.5} borderRadius="md"
                  border="1px solid" borderColor={c.border}
                  color={c.text} fontSize="xs" fontWeight="700"
                  cursor={refreshing ? 'not-allowed' : 'pointer'}
                  opacity={refreshing ? 0.6 : 1}
                  _hover={refreshing ? {} : { bg: c.bgHover, borderColor: brand.green, color: brand.green }}
                  onClick={handleRefreshExternal}
                  disabled={refreshing}
                  title="Re-consulta SUNAT, REINFO y RECPO para el RUC del proveedor y stagea los campos que cambiaron"
                >
                  <Box as="span" style={{ animation: refreshing ? 'spin 1s linear infinite' : undefined }}>
                    <LuRefreshCw size={14} />
                  </Box>
                  {refreshing ? 'Consultando RUC…' : 'Actualizar datos RUC'}
                </Box>
              )}
            </Flex>
            <Text fontSize="3xl" fontWeight="800" color={c.accent} fontFamily="heading">
              {reviewMode ? 'Revisión de cambios' : currentStep.title}
            </Text>
            <Text color={c.textSecondary} mt={2} maxW="2xl">
              {reviewMode ? `Motivo: ${mr.reason}` : currentStep.description}
            </Text>
          </Box>

          <Box flex={1} display="flex" flexDirection="column">
            {reviewMode ? (
              <ReviewDiffView changes={changes} proveedor={proveedor} />
            ) : (
              <>
                {activeTab === 'identidad' && (
                  <>
                    <Step1Identity
                      ref={activeStepRef}
                      mode="edit"
                      // Con lo ya stageado encima: si no, tras guardar el formulario volvía a
                      // los valores originales y el siguiente "Guardar cambios" borraba del
                      // cambio todo campo que coincidiera con el original (se perdía lo anterior).
                      defaultValues={{
                        ruc: proveedor.ruc,
                        legalName: proveedor.legalName,
                        commercialName: stagedCompany.commercialName ?? proveedor.commercialName ?? '',
                        taxAddress: stagedCompany.taxAddress ?? proveedor.taxAddress ?? '',
                        ubigeoCode: stagedCompany.ubigeoCode ?? proveedor.ubigeoCode ?? '',
                        mainPhone: stagedCompany.mainPhone ?? proveedor.mainPhone ?? '',
                        mainEmail: stagedCompany.mainEmail ?? proveedor.mainEmail ?? '',
                        website: stagedCompany.website ?? proveedor.website ?? '',
                        prefix: proveedor.prefix ?? '',
                      }}
                      initialConsultaData={{
                        economicActivities: proveedor.economicActivities ?? [],
                        taxpayerStatus: proveedor.taxpayerStatus,
                        taxpayerCondition: proveedor.taxpayerCondition,
                        taxpayerType: proveedor.taxpayerType,
                      }}
                      companyId={proveedor.id}
                      persons={overlaidPersons}
                      onPersonsChange={() => { /* state comes from overlay on next load */ }}
                      addPerson={stagePersonAdd}
                      removePerson={stagePersonRemove}
                    />
                    <PowerValiditiesSection
                      mode="edit"
                      ruc={proveedor.ruc}
                      persons={overlaidPersons}
                      powerValidities={overlaidPowerValidities}
                      onAdd={stagePowerValidityAdd}
                      onRemove={stagePowerValidityRemove}
                    />
                  </>
                )}
                {activeTab === 'acreditacion' && (
                  <Step2Accreditation
                    ref={activeStepRef}
                    mode="edit"
                    defaultCategoryId={overlaidMiningSupplier.categoryId ?? 0}
                  />
                )}
                {activeTab === 'contrato' && (
                  <Step2bContrato
                    mode={isDraft ? 'edit' : 'readonly'}
                    activeContract={overlaidContract.active}
                    contractHistory={overlaidContract.history}
                    onUpload={stageContractUpload}
                  />
                )}
                {activeTab === 'derechos' && (
                  <>
                    <Flex justifyContent="flex-end" mb={3}>
                      <Box as="button" type="button"
                        display="inline-flex" alignItems="center" gap={2}
                        px={3} py={1.5} borderRadius="md"
                        border="1px solid" borderColor={c.border}
                        color={c.text} fontSize="xs" fontWeight="700"
                        cursor={refreshingDms ? 'not-allowed' : 'pointer'}
                        opacity={refreshingDms ? 0.6 : 1}
                        _hover={refreshingDms ? {} : { bg: c.bgHover, borderColor: brand.green, color: brand.green }}
                        onClick={handleRefreshDms}
                        disabled={refreshingDms}
                        title="Re-consulta REINFO y mineros formales para cada DM del proveedor y stagea los campos que cambiaron"
                      >
                        <Box as="span" style={{ animation: refreshingDms ? 'spin 1s linear infinite' : undefined }}>
                          <LuRefreshCw size={14} />
                        </Box>
                        {refreshingDms ? 'Consultando DMs…' : 'Actualizar DMs'}
                      </Box>
                    </Flex>
                    <Step3MiningRights
                      mode="edit"
                      companyId={proveedor.id}
                      savedMiningRights={overlaidMiningRights}
                      onSavePolygon={stagePolygonSave}
                      onAddComponent={stageComponentAdd}
                      onRemoveComponent={stageComponentRemove}
                      onRefresh={async () => { await load() }}
                    />
                  </>
                )}
                {activeTab === 'igafom' && (
                  <Step4Igafom
                    mode="edit"
                    companyId={proveedor.id}
                    savedMiningRights={overlaidMiningRights}
                    minerSource={proveedor.miningSupplier?.minerSource}
                    onAdd={stageIgafomAdd}
                    onUpdate={stageIgafomUpdate}
                    onRemove={stageIgafomRemove}
                  />
                )}
                {activeTab === 'legaldocs' && (
                  <Step4bLegalDocuments
                    mode="edit"
                    companyId={proveedor.id}
                    savedMiningRights={overlaidMiningRights}
                    onAdd={stageLegalDocAdd}
                    onUpdate={stageLegalDocUpdate}
                    onRemove={stageLegalDocRemove}
                  />
                )}
                {activeTab === 'produccion' && (
                  <Step5Production
                    mode="edit"
                    companyId={proveedor.id}
                    savedMiningRights={overlaidMiningRights}
                    onAdd={stageProductionAdd}
                    onRemove={stageProductionRemove}
                  />
                )}
                {activeTab === 'iqbf' && (
                  <Step7Iqbf
                    mode={isDraft ? 'edit' : 'readonly'}
                    companyId={proveedor.id}
                    savedMiningRights={overlaidMiningRights}
                    onToggleUsesExplosives={stageUsesExplosivesToggle}
                    onAddAuthorization={stageAuthorizationAdd}
                    onUpdateAuthorization={stageAuthorizationUpdate}
                    onRemoveAuthorization={stageAuthorizationRemove}
                    onAddHandler={stageHandlerAdd}
                    onBulkAddHandlers={stageBulkHandlers}
                    onRemoveHandler={stageHandlerRemove}
                  />
                )}
                {activeTab === 'finanzas' && (
                  <Step8Finance
                    mode="edit"
                    bankAccounts={overlaidBankAccounts}
                    onAddAccount={stageBankAdd}
                    onRemoveAccount={stageBankRemove}
                    incomeTaxCategoryId={overlaidMiningSupplier.incomeTaxCategoryId ?? 0}
                    onIncomeTaxChange={stageIncomeTaxChange}
                    defaultSplaftDeclared={overlaidMiningSupplier.splaftDeclared ?? false}
                    onSplaftDeclaredChange={stageSplaftDeclared}
                    onSplaftFileChange={stageSplaftFile}
                    splaftDocumentFile={proveedor.splaftDocumentFile}
                    taxReports={overlaidTaxReports}
                    onAddTaxReport={stageTaxReportAdd}
                    onRemoveTaxReport={stageTaxReportRemove}
                  />
                )}
                {activeTab === 'accionistas' && (
                  <Step10Ubo
                    isReadonly={!isDraft}
                    shareholders={overlaidShareholders}
                    requiresEnhancedDueDiligence={requiresEnhancedDueDiligence}
                    onAddFromCertificate={stageShareholderAddFromCertificate}
                    onUpdate={stageShareholderUpdate}
                    onRemove={stageShareholderRemove}
                  />
                )}

                {changes.length > 0 && (
                  <Box mt={8} pt={6} borderTop="1px solid" borderColor={c.border}>
                    <Text fontSize="sm" fontWeight="700" color={c.text} mb={3}>
                      Cambios pendientes en esta solicitud ({changes.length})
                    </Text>
                    <Text fontSize="xs" color={c.textSecondary} mb={3}>
                      Revisa los cambios que se aplicarán al aprobar esta solicitud. Puedes quitar cambios individuales con el botón eliminar.
                    </Text>
                    <Flex direction="column" gap={3}>
                      {changes.map(change => (
                        <DiffCard
                          key={change.id}
                          change={change}
                          proveedor={proveedor}
                          componentTypeNames={componentTypeNames}
                          onRemove={async () => {
                            if (!mrId) return
                            await modificationRequestService.removeDraftChange(mrId, change.id)
                            await load()
                          }}
                        />
                      ))}
                    </Flex>
                  </Box>
                )}
              </>
            )}
          </Box>

          <Flex
            position="sticky" bottom={0} zIndex={10}
            bg={c.bgCard} px={8} py={4}
            justifyContent="space-between" alignItems="center"
            borderTop="1px solid" borderColor={c.border}
            mx={-6} mt={6}
          >
            <Box as="button" type="button"
              display="flex" alignItems="center" gap={2}
              color={c.accent} fontWeight="700" fontSize="sm" px={4} py={2}
              borderRadius="md" cursor="pointer" _hover={{ bg: c.bgHover }}
              onClick={() => navigate(`/app/proveedores/${proveedorId}/modificaciones`)}
            >
              <LuArrowLeft size={16} />
              Volver a modificaciones
            </Box>

            <Flex gap={3}>
              {!reviewMode && isFormTab && (
                <Box as="button" type="button"
                  display="flex" alignItems="center" gap={2}
                  px={4} py={2} borderRadius="md"
                  bg={brand.green} color="white" fontSize="sm" fontWeight="700"
                  cursor="pointer" _hover={{ opacity: 0.9 }}
                  onClick={activeTab === 'identidad' ? stageCompanyChanges : stageAcreditacionChanges}
                >
                  <LuSave size={14} />
                  Guardar cambios
                </Box>
              )}

              {(isDraft || isPending) && (
                <Box as="button" type="button"
                  display="flex" alignItems="center" gap={2}
                  px={4} py={2} borderRadius="md"
                  border="1px solid" borderColor="#b23a26" color="#b23a26"
                  fontSize="sm" fontWeight="700"
                  cursor={busy ? 'not-allowed' : 'pointer'}
                  opacity={busy ? 0.5 : 1}
                  _hover={busy ? {} : { bg: 'rgba(220, 38, 38, 0.08)' }}
                  onClick={() => setConfirming('cancel')}
                  disabled={busy}
                >
                  <LuX size={14} />
                  Anular solicitud
                </Box>
              )}
              {isDraft && (
                <Box as="button" type="button"
                  display="flex" alignItems="center" gap={2}
                  px={4} py={2} borderRadius="md"
                  bg={busy || changes.length === 0 ? '#b5aa9d' : brand.green} color="white"
                  fontSize="sm" fontWeight="700"
                  cursor={busy || changes.length === 0 ? 'not-allowed' : 'pointer'}
                  opacity={busy || changes.length === 0 ? 0.5 : 1}
                  _hover={busy || changes.length === 0 ? {} : { opacity: 0.9 }}
                  onClick={handleSubmit}
                  disabled={busy || changes.length === 0}
                >
                  <LuSend size={14} />
                  Enviar a revisión
                </Box>
              )}
              {isPending && (
                <PermissionGate permission="expedientes.approveModification" mode="hide">
                  <Flex gap={2}>
                    <Box as="button" type="button"
                      px={4} py={2} borderRadius="md"
                      bg="#b23a26" color="white" fontSize="sm" fontWeight="700"
                      cursor={busy ? 'not-allowed' : 'pointer'}
                      opacity={busy ? 0.5 : 1}
                      _hover={busy ? {} : { opacity: 0.9 }}
                      onClick={() => setConfirming('reject')}
                      disabled={busy}
                    >
                      Rechazar
                    </Box>
                    <Box as="button" type="button"
                      display="flex" alignItems="center" gap={2}
                      px={4} py={2} borderRadius="md"
                      bg={brand.green} color="white" fontSize="sm" fontWeight="700"
                      cursor={busy ? 'not-allowed' : 'pointer'}
                      opacity={busy ? 0.5 : 1}
                      _hover={busy ? {} : { opacity: 0.9 }}
                      onClick={() => setConfirming('approve')}
                      disabled={busy}
                    >
                      <LuShield size={14} />
                      Aprobar y aplicar
                    </Box>
                  </Flex>
                </PermissionGate>
              )}
            </Flex>
          </Flex>
        </Box>
      </Box>

      <ConfirmDialog open={confirming === 'cancel'} onClose={() => setConfirming(null)} onConfirm={runCancel}
        title="Anular solicitud" variant="danger" confirmLabel="Anular" cancelLabel="Volver" isLoading={busy}>
        Los cambios stageados se descartarán y la solicitud quedará marcada como anulada. Esta acción no se puede deshacer.
      </ConfirmDialog>
      <ConfirmDialog open={confirming === 'approve'} onClose={() => setConfirming(null)} onConfirm={runApprove}
        title="Aprobar solicitud" variant="default" confirmLabel="Aprobar y aplicar" cancelLabel="Cancelar" isLoading={busy}>
        Los {changes.length} cambios stageados se aplicarán a los datos reales y quedarán registrados en el expediente.
        <Textarea mt={3} size="sm" maxLength={1000} placeholder="Comentario (opcional)"
          value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} />
      </ConfirmDialog>
      <ConfirmDialog open={confirming === 'reject'} onClose={() => setConfirming(null)} onConfirm={runReject}
        title="Rechazar solicitud" variant="danger" confirmLabel="Rechazar" cancelLabel="Volver" isLoading={busy}>
        Los cambios stageados no se aplicarán. La solicitud quedará marcada como rechazada.
        <Textarea mt={3} size="sm" maxLength={1000} placeholder="Motivo del rechazo (obligatorio)"
          value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} />
      </ConfirmDialog>

      <ConfirmDialog
        open={refreshResult !== null}
        onClose={() => setRefreshResult(null)}
        onConfirm={() => setRefreshResult(null)}
        title={refreshResult === 'diffs' ? 'Se encontraron diferencias' : 'No se encontraron diferencias'}
        variant="default"
        confirmLabel="Entendido"
        cancelLabel=""
      >
        {refreshResult === 'diffs'
          ? 'Los campos que cambiaron respecto a SUNAT, REINFO y RECPO se agregaron a la lista de "Cambios pendientes".'
          : 'Los datos del proveedor ya están sincronizados con SUNAT, REINFO y RECPO. No hay campos que actualizar.'}
      </ConfirmDialog>

      <ConfirmDialog
        open={refreshDmsResult !== null}
        onClose={() => setRefreshDmsResult(null)}
        onConfirm={() => setRefreshDmsResult(null)}
        title={refreshDmsResult === 'diffs' ? 'Se encontraron diferencias en los DMs' : 'No se encontraron diferencias en los DMs'}
        variant="default"
        confirmLabel="Entendido"
        cancelLabel=""
      >
        {refreshDmsResult === 'diffs'
          ? 'Los DMs con cambios respecto a REINFO o mineros formales se agregaron a la lista de "Cambios pendientes".'
          : 'Los derechos mineros ya están sincronizados con REINFO y mineros formales. No hay cambios que actualizar.'}
      </ConfirmDialog>
    </Flex>
    </ModificationModeProvider>
  )
}

// ── Review diff view ─────────────────────────────────────────────────────────

function ReviewDiffView({ changes, proveedor }: { changes: DraftChangeResponse[]; proveedor: ProveedorResponse }) {
  const colors = themeColors
  if (changes.length === 0) return (
    <Box p={6} textAlign="center" borderRadius="xl" border="1px dashed" borderColor={colors.border}>
      <Text fontSize="sm" color={colors.textMuted}>Esta solicitud no tiene cambios stageados.</Text>
    </Box>
  )
  return (
    <Box>
      <Text fontSize="sm" fontWeight="700" color={colors.text} mb={3}>
        Cambios propuestos ({changes.length})
      </Text>
      <Flex direction="column" gap={3}>
        {changes.map(change => <DiffCard key={change.id} change={change} proveedor={proveedor} />)}
      </Flex>
    </Box>
  )
}

function DiffCard({ change, proveedor, onRemove, componentTypeNames }: { change: DraftChangeResponse; proveedor: ProveedorResponse; onRemove?: () => void | Promise<void>; componentTypeNames?: Record<string, string> }) {
  const colors = themeColors
  const [expanded, setExpanded] = useState(true)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [removing, setRemoving] = useState(false)
  const opLabel = change.operation === 0 ? 'Agregar' : change.operation === 1 ? 'Modificar' : 'Eliminar'
  const opColor = change.operation === 0 ? '#1d6664' : change.operation === 1 ? '#a8740e' : '#b23a26'

  let payload: Record<string, unknown> | null = null
  try { payload = JSON.parse(change.proposedSnapshotJson) as Record<string, unknown> } catch { payload = null }

  const mrId = change.modificationRequestId
  const body = payload == null
    ? <Text fontSize="xs" color={colors.textMuted}>No se pudo parsear el payload</Text>
    : change.entityName === 'IgafomRecord' ? <IgafomDiffBody payload={payload} proveedor={proveedor} entityId={change.entityId} operation={change.operation} mrId={mrId} />
    : change.entityName === 'LegalDocumentRecord' ? <LegalDocumentDiffBody payload={payload} proveedor={proveedor} entityId={change.entityId} operation={change.operation} mrId={mrId} />
    : change.entityName === 'BankAccount' ? <BankAccountDiffBody payload={payload} proveedor={proveedor} entityId={change.entityId} operation={change.operation} />
    : change.entityName === 'MiningSupplier' ? <MiningSupplierDiffBody payload={payload} proveedor={proveedor} operation={change.operation} mrId={mrId} />
    : change.entityName === 'IqbfRecord' ? <IqbfRecordDiffBody payload={payload} proveedor={proveedor} operation={change.operation} mrId={mrId} />
    : change.entityName === 'ProductionDeclaration' ? <ProductionDiffBody payload={payload} proveedor={proveedor} entityId={change.entityId} operation={change.operation} mrId={mrId} />
    : change.entityName === 'MiningRight' ? <MiningRightDiffBody payload={payload} proveedor={proveedor} entityId={change.entityId} operation={change.operation} />
    : change.entityName === 'MiningRightComponent' ? <MiningRightComponentDiffBody payload={payload} proveedor={proveedor} entityId={change.entityId} operation={change.operation} componentTypeNames={componentTypeNames} />
    : change.entityName === 'Company' ? <CompanyDiffBody payload={payload} proveedor={proveedor} operation={change.operation} />
    : change.entityName === 'CompanyPerson' ? <CompanyPersonDiffBody payload={payload} proveedor={proveedor} entityId={change.entityId} operation={change.operation} />
    : change.entityName === 'SupplierContract' ? <ContractDiffBody payload={payload} />
    : change.entityName === 'ExplosiveAuthorization' ? <ExplosiveAuthorizationDiffBody payload={payload} proveedor={proveedor} entityId={change.entityId} operation={change.operation} />
    : change.entityName === 'ExplosiveHandler' ? <ExplosiveHandlerDiffBody payload={payload} proveedor={proveedor} entityId={change.entityId} operation={change.operation} />
    : change.entityName === 'TaxReport' ? <TaxReportDiffBody payload={payload} proveedor={proveedor} entityId={change.entityId} operation={change.operation} mrId={mrId} />
    : change.entityName === 'PowerValidity' ? <PowerValidityDiffBody payload={payload} proveedor={proveedor} entityId={change.entityId} operation={change.operation} mrId={mrId} />
    : change.entityName === 'SupplierShareholder' ? <ShareholderDiffBody payload={payload} proveedor={proveedor} entityId={change.entityId} operation={change.operation} />
    : <GenericDiffBody payload={payload} operation={change.operation} />

  return (
    <Box border="1px solid" borderColor={colors.border} borderRadius="xl" bg={colors.bgCard}>
      <Flex p={3} alignItems="center" gap={3}>
        <Box color={colors.textMuted} cursor="pointer" onClick={() => setExpanded(e => !e)}>
          {expanded ? <LuChevronDown size={16} /> : <LuChevronRight size={16} />}
        </Box>
        <Box px={2} py={0.5} borderRadius="md" fontSize="10px" fontWeight="bold" bg={`${opColor}20`} color={opColor}>
          {opLabel.toUpperCase()}
        </Box>
        <Text fontSize="sm" fontWeight="600" color={colors.text} flex={1} cursor="pointer" onClick={() => setExpanded(e => !e)}>
          {entityDisplayName(change.entityName)}
          {change.entityId && <Text as="span" ml={2} fontSize="xs" color={colors.textMuted} fontFamily="monospace">{change.entityId.substring(0, 8)}…</Text>}
        </Text>
        {onRemove && (
          <Box as="button" type="button"
            color={colors.textMuted}
            cursor="pointer"
            _hover={{ color: '#b23a26' }}
            onClick={() => setConfirmRemove(true)}
            title="Quitar este cambio del staging"
          >
            <LuX size={16} />
          </Box>
        )}
      </Flex>
      {expanded && <Box px={4} py={3} borderTop="1px solid" borderColor={colors.border}>{body}</Box>}

      {onRemove && (
        <ConfirmDialog
          open={confirmRemove}
          onClose={() => { if (!removing) setConfirmRemove(false) }}
          onConfirm={async () => {
            setRemoving(true)
            try { await onRemove() } finally { setRemoving(false); setConfirmRemove(false) }
          }}
          title="Quitar cambio pendiente"
          variant="danger"
          confirmLabel="Quitar"
          cancelLabel="Cancelar"
          isLoading={removing}
        >
          Este cambio dejará de formar parte de la solicitud. No se aplicará al aprobarla. ¿Deseas continuar?
        </ConfirmDialog>
      )}
    </Box>
  )
}

function entityDisplayName(entityName: string): string {
  switch (entityName) {
    case 'IgafomRecord': return 'IGAFOM'
    case 'LegalDocumentRecord': return 'Documento legal'
    case 'BankAccount': return 'Cuenta bancaria'
    case 'MiningSupplier': return 'Datos tributarios / Acreditación'
    case 'IqbfRecord': return 'IQBF'
    case 'ProductionDeclaration': return 'Declaración de producción'
    case 'MiningRight': return 'Derecho minero'
    case 'MiningRightComponent': return 'Componente minero'
    case 'Company': return 'Datos de empresa'
    case 'CompanyPerson': return 'Contacto'
    case 'SupplierContract': return 'Contrato'
    case 'ExplosiveAuthorization': return 'Autorización de explosivos'
    case 'ExplosiveHandler': return 'Manipulador de explosivos'
    case 'TaxReport': return 'Reporte tributario'
    case 'PowerValidity': return 'Vigencia de poder'
    case 'SupplierShareholder': return 'Accionista'
    default: return entityName
  }
}

function ContractDiffBody({ payload }: { payload: Record<string, unknown> }) {
  const rows: DiffRow[] = []
  if (payload.documentFileName != null) rows.push({ label: 'Documento', after: payload.documentFileName as string })
  if (payload.contractDate != null) rows.push({ label: 'Fecha del contrato', after: payload.contractDate as string })
  if (payload.expirationDate != null) rows.push({ label: 'Fecha de caducidad', after: payload.expirationDate as string })
  if (payload.notes != null) rows.push({ label: 'Notas', after: payload.notes as string })
  return <DiffTable rows={rows} operation={0} />
}

function ExplosiveAuthorizationDiffBody({ payload, proveedor, entityId, operation }: { payload: Record<string, unknown>; proveedor: ProveedorResponse; entityId: string | null; operation: number }) {
  // Modificación/baja: se compara contra la autorización vigente. Antes la columna "Antes"
  // salía vacía y el revisor no veía qué cambiaba.
  const owner = entityId ? proveedor.miningRights?.find(m => m.explosiveAuthorizations?.some(a => a.id === entityId)) : undefined
  const existing = owner?.explosiveAuthorizations?.find(a => a.id === entityId)
  const miningRight = proveedor.miningRights?.find(m => m.id === (payload.miningRightId as string | undefined)) ?? owner
  const typeOf = (t: unknown) => (t == null ? undefined : enumLabel(EXPLOSIVE_AUTHORIZATION_TYPES, Number(t)))
  const rows: DiffRow[] = [
    { label: 'Derecho minero', before: owner?.name, after: miningRight?.name },
    { label: 'Tipo', before: typeOf(existing?.authorizationType), after: typeOf(payload.authorizationType) },
    { label: 'N° de resolución', before: existing?.resolutionNumber ?? undefined, after: payload.resolutionNumber as string | undefined },
    { label: 'Fecha de emisión', before: existing?.emissionDate ?? undefined, after: payload.emissionDate as string | undefined },
    { label: 'Fecha de vencimiento', before: existing?.expirationDate ?? undefined, after: payload.expirationDate as string | undefined },
    { label: 'Explosivos autorizados', before: existing?.authorizedExplosives ?? undefined, after: payload.authorizedExplosives as string | undefined },
    { label: 'Capacidad máxima', before: existing?.maxCapacity ?? undefined, after: payload.maxCapacity as string | undefined },
    { label: 'Documento', after: payload.documentFileName as string | undefined },
  ]
  return <DiffTable rows={rows} operation={operation} />
}

function ExplosiveHandlerDiffBody({ payload, proveedor, entityId, operation }: { payload: Record<string, unknown>; proveedor: ProveedorResponse; entityId: string | null; operation: number }) {
  const miningRightId = payload.miningRightId as string | undefined
  const miningRight = proveedor.miningRights?.find(m => m.id === miningRightId)
  const rows: DiffRow[] = []
  if (miningRight) rows.push({ label: 'Derecho minero', after: miningRight.name })
  if (operation === 2) {
    const existing = entityId ? proveedor.miningRights?.flatMap(m => m.explosiveHandlers ?? []).find(h => h.id === entityId) : null
    if (existing) {
      rows.push({ label: 'Nombre', after: existing.fullName })
      if (existing.identityDoc) rows.push({ label: 'Documento', after: existing.identityDoc })
    }
  } else {
    const fullName = [payload.names, payload.paternalSurname, payload.maternalSurname].filter(Boolean).join(' ')
    if (fullName) rows.push({ label: 'Nombre', after: fullName })
    if (payload.identityDoc != null) rows.push({ label: 'Documento', after: payload.identityDoc as string })
    if (payload.licenseNumber != null) rows.push({ label: 'N° de carné', after: payload.licenseNumber as string })
    if (payload.expirationDate != null) rows.push({ label: 'Vencimiento del carné', after: payload.expirationDate as string })
  }
  return <DiffTable rows={rows} operation={operation} />
}

// Etiquetas de ContactType (MineTrace.Shared.Enums.ContactType). Antes el diff mostraba el número.
const CONTACT_TYPE_LABELS: Record<number, string> = {
  1: 'Representante Legal', 2: 'Contacto Administrativo', 3: 'Contacto Comercial',
  4: 'Contacto Financiero', 5: 'Contacto Técnico',
}

function CompanyPersonDiffBody({ payload, proveedor, entityId, operation }: { payload: Record<string, unknown>; proveedor: ProveedorResponse; entityId: string | null; operation: number }) {
  const existing = entityId ? proveedor.persons?.find(p => p.id === entityId) : null
  const rows: DiffRow[] = []
  if (operation === 2) {
    // Delete — show identity of the person being removed
    if (existing) {
      rows.push({ label: 'Nombre', after: existing.fullName })
      rows.push({ label: 'Documento', after: existing.identityDoc ?? '—' })
      if (existing.email) rows.push({ label: 'Email', after: existing.email })
    }
  } else {
    const fullName = [payload.names, payload.paternalSurname, payload.maternalSurname].filter(Boolean).join(' ')
    if (fullName) rows.push({ label: 'Nombre', after: fullName })
    if (payload.identityDoc != null) rows.push({ label: 'Documento', after: payload.identityDoc as string })
    if (payload.contactType != null) rows.push({ label: 'Tipo de contacto', after: CONTACT_TYPE_LABELS[Number(payload.contactType)] ?? String(payload.contactType) })
    if (payload.email != null) rows.push({ label: 'Email', after: payload.email as string })
    if (payload.phoneMobile != null) rows.push({ label: 'Teléfono', after: payload.phoneMobile as string })
  }
  return <DiffTable rows={rows} operation={operation} />
}

// ── Diff bodies ──────────────────────────────────────────────────────────────

function IgafomDiffBody({ payload, proveedor, entityId, operation, mrId }: { payload: Record<string, unknown>; proveedor: ProveedorResponse; entityId: string | null; operation: number; mrId: string }) {
  const miningRightId = payload.miningRightId as string | undefined
  const miningRight = proveedor.miningRights?.find(m => m.id === miningRightId)
  const existing = entityId ? proveedor.miningRights?.flatMap(m => m.igafomRecords ?? []).find(r => r.id === entityId) : null
  const rows: DiffRow[] = [
    { label: 'Derecho minero', before: existing ? proveedor.miningRights?.find(m => m.igafomRecords?.some(r => r.id === entityId))?.name : undefined, after: miningRight?.name },
    { label: 'Fecha de presentación', before: existing?.presentationDate ?? undefined, after: payload.presentationDate as string | undefined },
    { label: 'Fecha de caducidad', before: existing?.expirationDate ?? undefined, after: payload.expirationDate as string | undefined },
    { label: 'Número de expediente', before: existing?.fileNumber ?? undefined, after: payload.fileNumber as string | undefined },
    { label: 'Tipo de IGAFOM', before: existing?.type ? enumLabel(IGAFOM_TYPES, existing.type) : undefined, after: payload.type ? enumLabel(IGAFOM_TYPES, payload.type as number) : undefined },
    { label: 'Método de explotación / procesamiento', before: existing?.exploitationMethod ? enumLabel(ALL_MINING_METHODS, existing.exploitationMethod) : undefined, after: payload.exploitationMethod ? enumLabel(ALL_MINING_METHODS, payload.exploitationMethod as number) : undefined },
    { label: 'Estado del documento', before: existing?.documentStatus ? enumLabel(IGAFOM_DOCUMENT_STATUSES, existing.documentStatus) : undefined, after: payload.documentStatus ? enumLabel(IGAFOM_DOCUMENT_STATUSES, payload.documentStatus as number) : undefined },
    { label: 'Sustancia', before: existing?.substanceId ? enumLabel(MINERAL_SUBSTANCES, existing.substanceId) : undefined, after: payload.substanceId ? enumLabel(MINERAL_SUBSTANCES, payload.substanceId as number) : undefined },
    { label: 'Producción diaria estimada', before: existing?.productiveCapacity ?? undefined, after: payload.productiveCapacity as string | undefined },
    {
      label: 'Documento IGAFOM',
      afterNode: payload.documentFileAttachmentId
        ? <StagedFileDownload mrId={mrId} fileId={payload.documentFileAttachmentId as string} label="IGAFOM-documento.pdf" />
        : undefined,
    },
    {
      label: 'Acta de recepción',
      afterNode: payload.receiptFileAttachmentId
        ? <StagedFileDownload mrId={mrId} fileId={payload.receiptFileAttachmentId as string} label="IGAFOM-acta.pdf" />
        : undefined,
    },
  ]
  return <DiffTable rows={rows} operation={operation} />
}

function LegalDocumentDiffBody({ payload, proveedor, entityId, operation, mrId }: { payload: Record<string, unknown>; proveedor: ProveedorResponse; entityId: string | null; operation: number; mrId: string }) {
  const miningRightId = payload.miningRightId as string | undefined
  const miningRight = proveedor.miningRights?.find(m => m.id === miningRightId)
  const existing = entityId ? proveedor.miningRights?.flatMap(m => m.legalDocumentRecords ?? []).find(r => r.id === entityId) : null
  const rows: DiffRow[] = [
    { label: 'Derecho minero', before: existing ? proveedor.miningRights?.find(m => m.legalDocumentRecords?.some(r => r.id === entityId))?.name : undefined, after: miningRight?.name },
    { label: 'Tipo de documento', before: existing?.documentType ? enumLabel(LEGAL_DOCUMENT_TYPES, existing.documentType) : undefined, after: payload.documentType ? enumLabel(LEGAL_DOCUMENT_TYPES, payload.documentType as number) : undefined },
    { label: 'Fecha de emisión', before: existing?.presentationDate ?? undefined, after: payload.presentationDate as string | undefined },
    { label: 'Fecha de caducidad', before: existing?.expirationDate ?? undefined, after: payload.expirationDate as string | undefined },
    { label: 'Número de expediente', before: existing?.fileNumber ?? undefined, after: payload.fileNumber as string | undefined },
    { label: 'Estado del documento', before: existing?.documentStatus ? enumLabel(IGAFOM_DOCUMENT_STATUSES, existing.documentStatus) : undefined, after: payload.documentStatus ? enumLabel(IGAFOM_DOCUMENT_STATUSES, payload.documentStatus as number) : undefined },
    {
      label: 'Documento',
      afterNode: payload.documentFileAttachmentId
        ? <StagedFileDownload mrId={mrId} fileId={payload.documentFileAttachmentId as string} label="documento-legal.pdf" />
        : undefined,
    },
    {
      label: 'Cargo de presentación',
      afterNode: payload.receiptFileAttachmentId
        ? <StagedFileDownload mrId={mrId} fileId={payload.receiptFileAttachmentId as string} label="cargo-presentacion.pdf" />
        : undefined,
    },
  ]
  return <DiffTable rows={rows} operation={operation} />
}

function BankAccountDiffBody({ payload, proveedor, entityId, operation }: { payload: Record<string, unknown>; proveedor: ProveedorResponse; entityId: string | null; operation: number }) {
  const existing = entityId ? proveedor.bankAccounts?.find(a => a.id === entityId) : null
  const rows: DiffRow[] = [
    { label: 'Número de cuenta', before: existing?.accountNumber, after: payload.accountNumber as string | undefined },
    { label: 'Banco', before: existing?.bankEntityId ? enumLabel(BANK_ENTITIES, existing.bankEntityId) : undefined, after: payload.bankEntityId ? enumLabel(BANK_ENTITIES, payload.bankEntityId as number) : undefined },
    { label: 'Tipo de cuenta', before: existing?.accountTypeId ? enumLabel(ACCOUNT_TYPES, existing.accountTypeId) : undefined, after: payload.accountTypeId ? enumLabel(ACCOUNT_TYPES, payload.accountTypeId as number) : undefined },
  ]
  return <DiffTable rows={rows} operation={operation} />
}

function MiningSupplierDiffBody({ payload, proveedor, operation, mrId }: { payload: Record<string, unknown>; proveedor: ProveedorResponse; operation: number; mrId: string }) {
  const ms = proveedor.miningSupplier
  const rows: DiffRow[] = []
  if (payload.categoryId != null) rows.push({ label: 'Categoría minera', before: ms?.categoryId ? enumLabel(MINING_CATEGORIES, ms.categoryId) : undefined, after: enumLabel(MINING_CATEGORIES, payload.categoryId as number) })
  if (payload.incomeTaxCategoryId != null) rows.push({ label: 'Categoría de impuesto a la renta', before: proveedor.miningSupplier?.incomeTaxCategoryId ? enumLabel(INCOME_TAX_CATEGORIES, proveedor.miningSupplier.incomeTaxCategoryId) : undefined, after: enumLabel(INCOME_TAX_CATEGORIES, payload.incomeTaxCategoryId as number) })
  if (payload.legalCondition != null) rows.push({ label: 'Condición legal', after: enumLabel(LEGAL_CONDITIONS, payload.legalCondition as number) })
  if (payload.reinfoStatus != null) rows.push({ label: 'Estado REINFO', before: proveedor.reinfoStatus ?? undefined, after: payload.reinfoStatus as string })
  if (payload.minerSource != null) rows.push({ label: 'Fuente de minero', before: ms?.minerSource ?? undefined, after: payload.minerSource as string })
  if (payload.activityStartDate != null) rows.push({ label: 'Inicio de actividades', after: payload.activityStartDate as string })
  if (payload.reinfoInscriptionDate != null) rows.push({ label: 'Inscripción REINFO', after: payload.reinfoInscriptionDate as string })
  if (payload.splaftDeclared != null) rows.push({ label: 'SPLAFT declarado', before: proveedor.splaftDeclared ? 'Sí' : 'No', after: (payload.splaftDeclared as boolean) ? 'Sí' : 'No' })
  if (payload.splaftDocumentFileAttachmentId != null) rows.push({
    label: 'Documento SPLAFT',
    afterNode: <StagedFileDownload mrId={mrId} fileId={payload.splaftDocumentFileAttachmentId as string} label="SPLAFT.pdf" />,
  })
  return <DiffTable rows={rows} operation={operation} />
}

// El diff mostraba el estado como número ("3") y sin valor anterior.
function miningRightStatusLabel(id: number | null | undefined): string | undefined {
  if (id == null) return undefined
  return MINING_RIGHT_STATUSES.find((st) => st.value === id)?.label ?? String(id)
}

function CompanyDiffBody({ payload, proveedor, operation }: { payload: Record<string, unknown>; proveedor: ProveedorResponse; operation: number }) {
  const rows: DiffRow[] = []
  if (payload.commercialName != null) rows.push({ label: 'Nombre comercial', before: proveedor.commercialName ?? undefined, after: payload.commercialName as string })
  if (payload.taxAddress != null) rows.push({ label: 'Dirección fiscal', before: proveedor.taxAddress ?? undefined, after: payload.taxAddress as string })
  if (payload.ubigeoCode != null) rows.push({ label: 'Ubigeo', before: proveedor.ubigeoCode ? resolveUbigeoLabel(proveedor.ubigeoCode) : undefined, after: resolveUbigeoLabel(payload.ubigeoCode as string) })
  if (payload.mainPhone != null) rows.push({ label: 'Teléfono', before: proveedor.mainPhone ?? undefined, after: payload.mainPhone as string })
  if (payload.mainEmail != null) rows.push({ label: 'Email', before: proveedor.mainEmail ?? undefined, after: payload.mainEmail as string })
  if (payload.website != null) rows.push({ label: 'Sitio web', before: proveedor.website ?? undefined, after: payload.website as string })
  if (payload.taxpayerStatus != null) rows.push({ label: 'Estado contribuyente', before: proveedor.taxpayerStatus ?? undefined, after: payload.taxpayerStatus as string })
  if (payload.taxpayerCondition != null) rows.push({ label: 'Condición contribuyente', before: proveedor.taxpayerCondition ?? undefined, after: payload.taxpayerCondition as string })
  if (payload.taxpayerType != null) rows.push({ label: 'Tipo contribuyente', before: proveedor.taxpayerType ?? undefined, after: payload.taxpayerType as string })
  if (payload.economicActivities != null) {
    const raw = payload.economicActivities as string
    let parsed: string[] = []
    try { parsed = JSON.parse(raw) } catch { parsed = [raw] }
    const currentActivities = proveedor.economicActivities ?? []
    rows.push({
      label: 'Actividades económicas',
      before: currentActivities.length > 0 ? currentActivities.join(' · ') : undefined,
      after: parsed.join(' · '),
    })
  }
  return <DiffTable rows={rows} operation={operation} />
}

function IqbfRecordDiffBody({ payload, proveedor, operation, mrId }: { payload: Record<string, unknown>; proveedor: ProveedorResponse; operation: number; mrId: string }) {
  const iq = proveedor.miningSupplier?.iqbfRecord
  const rows: DiffRow[] = []
  if (payload.usesExplosives != null) rows.push({ label: 'Usa explosivos', before: iq ? (iq.usesExplosives ? 'Sí' : 'No') : undefined, after: (payload.usesExplosives as boolean) ? 'Sí' : 'No' })
  if (payload.otsPresentationDate != null) rows.push({ label: 'Fecha OTS', before: iq?.otsPresentationDate ?? undefined, after: payload.otsPresentationDate as string })
  if (payload.otsExpirationDate != null) rows.push({ label: 'Vencimiento OTS', before: iq?.otsExpirationDate ?? undefined, after: payload.otsExpirationDate as string })
  if (payload.otsStatus != null) rows.push({ label: 'Estado OTS', before: iq?.otsStatus ? enumLabel(OTS_STATUSES, iq.otsStatus) : undefined, after: enumLabel(OTS_STATUSES, payload.otsStatus as number) })
  if (payload.otsDocumentFileAttachmentId != null) rows.push({
    label: 'Documento OTS',
    afterNode: <StagedFileDownload mrId={mrId} fileId={payload.otsDocumentFileAttachmentId as string} label="IQBF-OTS.pdf" />,
  })
  if (payload.comDocumentFileAttachmentId != null) rows.push({
    label: 'Documento COM',
    afterNode: <StagedFileDownload mrId={mrId} fileId={payload.comDocumentFileAttachmentId as string} label="IQBF-COM.pdf" />,
  })
  return <DiffTable rows={rows} operation={operation} />
}

function ProductionDiffBody({ payload, proveedor, entityId, operation, mrId }: { payload: Record<string, unknown>; proveedor: ProveedorResponse; entityId: string | null; operation: number; mrId: string }) {
  const miningRightId = payload.miningRightId as string | undefined
  const miningRight = proveedor.miningRights?.find(m => m.id === miningRightId)
  const existing = entityId ? proveedor.miningRights?.flatMap(m => m.productionDeclarations ?? []).find(d => d.id === entityId) : null
  const rows: DiffRow[] = [
    { label: 'Derecho minero', after: miningRight?.name },
    { label: 'Período', before: existing?.period ? enumLabel(PRODUCTION_PERIODS, existing.period) : undefined, after: payload.period ? enumLabel(PRODUCTION_PERIODS, payload.period as number) : undefined },
    { label: 'Año', before: existing?.year?.toString(), after: (payload.year as number | undefined)?.toString() },
    { label: 'Fecha presentación', before: existing?.presentationDate ?? undefined, after: payload.presentationDate as string | undefined },
    { label: 'Número expediente', before: existing?.fileNumber ?? undefined, after: payload.fileNumber as string | undefined },
    { label: 'Producción diaria', before: existing?.dailyEstimatedProduction?.toString(), after: (payload.dailyEstimatedProduction as number | undefined)?.toString() },
    { label: 'Total metálico', before: existing?.totalMetallic?.toString(), after: (payload.totalMetallic as number | undefined)?.toString() },
    { label: 'Total no metálico', before: existing?.totalNonMetallic?.toString(), after: (payload.totalNonMetallic as number | undefined)?.toString() },
    {
      label: 'Documento',
      afterNode: payload.documentFileAttachmentId
        ? <StagedFileDownload mrId={mrId} fileId={payload.documentFileAttachmentId as string} label="DPS.pdf" />
        : undefined,
    },
  ]
  return <DiffTable rows={rows} operation={operation} />
}

function MiningRightDiffBody({ payload, proveedor, entityId, operation }: { payload: Record<string, unknown>; proveedor: ProveedorResponse; entityId: string | null; operation: number }) {
  const colors = themeColors
  const existing = entityId ? proveedor.miningRights?.find(m => m.id === entityId) : null
  const rows: DiffRow[] = []
  if (payload.name != null) rows.push({ label: 'Nombre', before: existing?.name, after: payload.name as string })
  if (payload.ubigeoCode != null) rows.push({ label: 'Ubigeo', before: existing?.ubigeoCode ? resolveUbigeoLabel(existing.ubigeoCode) : undefined, after: resolveUbigeoLabel(payload.ubigeoCode as string) })
  if (payload.miningRightStatusId != null) rows.push({ label: 'Estado del DM', before: miningRightStatusLabel(existing?.miningRightStatusId), after: miningRightStatusLabel(Number(payload.miningRightStatusId)) })
  if (payload.startDate != null) rows.push({ label: 'Fecha de inicio', before: existing?.startDate ?? undefined, after: payload.startDate as string })
  if (payload.boundaryWkt != null) {
    const wkt = payload.boundaryWkt as string
    // Vértices del WKT nuevo: "POLYGON((x1 y1, ...))" → N-1 únicos (el último repite el primero).
    const coordsMatch = wkt.match(/POLYGON\s*\(\s*\(([^)]+)\)\s*\)/i)
    const newCount = coordsMatch ? coordsMatch[1]!.split(',').length - 1 : null
    // Vértices del polígono actual (geoJson del DM) para el "antes".
    let beforeCount: number | null = null
    if (existing?.geoJson) {
      try {
        const geo = JSON.parse(existing.geoJson)
        if (geo?.type === 'Polygon' && Array.isArray(geo.coordinates?.[0])) beforeCount = geo.coordinates[0].length - 1
      } catch { /* */ }
    }
    rows.push({
      label: 'Polígono',
      before: beforeCount != null ? `${beforeCount} vértices` : (existing?.hasBoundary ? 'definido' : 'sin polígono'),
      after: newCount != null ? `${newCount} vértices (modificado)` : 'modificado',
    })
  }
  return (
    <Box>
      {existing?.name && (
        <Box mb={3} pb={2} borderBottom="1px solid" borderColor={colors.border}>
          <Text fontSize="xs" color={colors.textMuted} fontWeight="700" textTransform="uppercase" letterSpacing="0.05em" mb={0.5}>
            Derecho minero afectado
          </Text>
          <Text fontSize="sm" fontWeight="600" color={colors.text}>
            {existing.name}{existing.uniqueCode ? ` · ${existing.uniqueCode}` : ''}
          </Text>
        </Box>
      )}
      <DiffTable rows={rows} operation={operation} />
    </Box>
  )
}

function MiningRightComponentDiffBody({ payload, proveedor, entityId, operation, componentTypeNames }: { payload: Record<string, unknown>; proveedor: ProveedorResponse; entityId: string | null; operation: number; componentTypeNames?: Record<string, string> }) {
  // En DELETE el payload es { id }; ubicamos el componente real en su DM para describir qué se elimina.
  const found = entityId
    ? proveedor.miningRights
        ?.flatMap(m => (m.components ?? []).map(c => ({ comp: c, mrName: m.name })))
        .find(x => x.comp.id === entityId)
    : null
  const existing = found?.comp ?? null
  const mrId = payload.miningRightId as string | undefined
  const mrName = proveedor.miningRights?.find(m => m.id === mrId)?.name ?? found?.mrName
  // Nombre del tipo: 1) guardado en el payload al stagear, 2) catálogo cargado, 3) del componente existente.
  const typeId = payload.componentTypeId as string | undefined
  const typeName = (payload.componentTypeName as string | undefined)
    ?? (typeId ? componentTypeNames?.[typeId] : undefined)
    ?? existing?.componentTypeName
    ?? undefined
  // Coordenadas en UTM (Norte/Este), como las digita el usuario. El payload de alta
  // guarda northing/easting; el componente existente (delete) también los tiene.
  const northing = (payload.northing as number | undefined) ?? existing?.northing
  const easting = (payload.easting as number | undefined) ?? existing?.easting
  const coords = northing != null && Number.isFinite(northing) && easting != null && Number.isFinite(easting)
    ? `N: ${Math.round(northing).toLocaleString('en')} · E: ${Math.round(easting).toLocaleString('en')}`
    : undefined
  const rows: DiffRow[] = [
    { label: 'Derecho minero', after: mrName },
    { label: 'Tipo de componente', after: typeName },
    { label: 'Nombre', after: (payload.name as string | undefined) ?? existing?.name ?? undefined },
    { label: 'Coordenadas (UTM)', after: coords },
  ]
  return <DiffTable rows={rows} operation={operation} />
}

function TaxReportDiffBody({ payload, proveedor, entityId, operation, mrId }: { payload: Record<string, unknown>; proveedor: ProveedorResponse; entityId: string | null; operation: number; mrId: string }) {
  const existing = entityId ? proveedor.taxReports?.find(r => r.id === entityId) : null
  const rows: DiffRow[] = []
  if (operation === 2 && existing) {
    if (existing.code) rows.push({ label: 'Código', after: existing.code })
    rows.push({ label: 'Fecha de emisión', after: existing.issueDate })
  } else {
    if (payload.code != null) rows.push({ label: 'Código', after: payload.code as string })
    if (payload.issueDate != null) rows.push({ label: 'Fecha de emisión', after: payload.issueDate as string })
    if (payload.documentFileAttachmentId != null) rows.push({
      label: 'Documento',
      afterNode: <StagedFileDownload mrId={mrId} fileId={payload.documentFileAttachmentId as string} label="reporte-tributario.pdf" />,
    })
  }
  return <DiffTable rows={rows} operation={operation} />
}

function PowerValidityDiffBody({ payload, proveedor, entityId, operation, mrId }: { payload: Record<string, unknown>; proveedor: ProveedorResponse; entityId: string | null; operation: number; mrId: string }) {
  const existing = entityId ? proveedor.powerValidities?.find(p => p.id === entityId) : null
  const personId = payload.companyPersonId as string | undefined
  const personName = proveedor.persons?.find(p => p.id === personId)?.fullName ?? existing?.personFullName
    ?? (payload.companyPersonDraftChangeId ? 'Representante legal agregado en esta solicitud' : undefined)
  const rows: DiffRow[] = []
  if (operation === 2 && existing) {
    rows.push({ label: 'Apoderado', after: existing.personFullName ?? undefined })
    rows.push({ label: 'Partida / Código', after: existing.code })
    rows.push({ label: 'Fecha de emisión', after: existing.issueDate })
  } else {
    if (personName) rows.push({ label: 'Apoderado', after: personName })
    if (payload.code != null) rows.push({ label: 'Partida / Código', after: payload.code as string })
    if (payload.issueDate != null) rows.push({ label: 'Fecha de emisión', after: payload.issueDate as string })
    if (payload.documentFileAttachmentId != null) rows.push({
      label: 'Documento',
      afterNode: <StagedFileDownload mrId={mrId} fileId={payload.documentFileAttachmentId as string} label="vigencia-de-poder.pdf" />,
    })
  }
  return <DiffTable rows={rows} operation={operation} />
}

function ShareholderDiffBody({ payload, proveedor, entityId, operation }: { payload: Record<string, unknown>; proveedor: ProveedorResponse; entityId: string | null; operation: number }) {
  const existing = entityId ? proveedor.shareholders?.find(s => s.id === entityId) : null
  const rows: DiffRow[] = []
  if (operation === 2 && existing) {
    rows.push({ label: 'Nombre', after: existing.fullName })
    rows.push({ label: 'Documento', after: existing.documentNumber })
    rows.push({ label: 'Participación', after: `${existing.participationPercent}%` })
  } else {
    const fullName = [payload.firstName, payload.lastName].filter(Boolean).join(' ')
    if (fullName) rows.push({ label: 'Nombre', before: existing?.fullName, after: fullName })
    if (payload.documentNumber != null) rows.push({ label: 'Documento', before: existing?.documentNumber, after: payload.documentNumber as string })
    if (payload.participationPercent != null) rows.push({ label: 'Participación', before: existing ? `${existing.participationPercent}%` : undefined, after: `${payload.participationPercent}%` })
    if (payload.certificateFileName != null) rows.push({ label: 'Certificado Único Laboral', after: payload.certificateFileName as string })
  }
  return <DiffTable rows={rows} operation={operation} />
}

// Fallback legible para cualquier entidad sin diff dedicado: humaniza claves y oculta
// ids internos/adjuntos, en vez de volcar el JSON crudo al validador.
function GenericDiffBody({ payload, operation }: { payload: Record<string, unknown>; operation: number }) {
  const HIDDEN = /(^id$|Id$|FileAttachmentId$|companyBusinessId|miningRightId|companyId|FileName$)/
  const humanize = (k: string) => k
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, ch => ch.toUpperCase())
    .trim()
  const fmt = (v: unknown): string | undefined => {
    if (v == null || v === '') return undefined
    if (typeof v === 'boolean') return v ? 'Sí' : 'No'
    if (typeof v === 'object') return undefined
    return String(v)
  }
  const rows: DiffRow[] = Object.entries(payload)
    .filter(([k]) => !HIDDEN.test(k))
    .map(([k, v]) => ({ label: humanize(k), after: fmt(v) }))
    .filter(r => r.after !== undefined)
  if (rows.length === 0) return <Text fontSize="xs" color={themeColors.textMuted}>Sin detalles para mostrar.</Text>
  return <DiffTable rows={rows} operation={operation} />
}

interface DiffRow { label: string; before?: string; after?: string; afterNode?: React.ReactNode }

function DiffTable({ rows: rawRows, operation }: { rows: DiffRow[]; operation: number }) {
  const colors = themeColors
  // Baja: el payload sólo trae el id, así que lo que se muestra es el valor ACTUAL del
  // registro. Antes las bajas de cuenta bancaria, IGAFOM y documento legal decían "Sin
  // detalles para mostrar" y el revisor aprobaba sin saber qué se eliminaba.
  const rows = operation === 2
    ? rawRows.map(r => (r.after || r.afterNode ? r : { ...r, after: r.before }))
    : rawRows
  const showBefore = operation === 1
  const visibleRows = rows.filter(r => showBefore ? (r.before || r.after || r.afterNode) : (r.after || r.afterNode))
  if (visibleRows.length === 0) return <Text fontSize="xs" color={colors.textMuted}>Sin detalles para mostrar.</Text>
  return (
    <Box>
      <Flex borderBottom="1px solid" borderColor={colors.border} px={2} py={1.5}
        fontSize="10px" fontWeight="700" color={colors.textMuted} textTransform="uppercase" letterSpacing="0.05em">
        <Box flex="1">Campo</Box>
        {showBefore && <Box flex="1">Antes</Box>}
        <Box flex="1">{operation === 2 ? 'Valor actual' : 'Nuevo valor'}</Box>
      </Flex>
      {visibleRows.map((r, i) => (
        <Flex key={i} px={2} py={2} borderBottom="1px solid" borderColor={colors.border} fontSize="sm" alignItems="center">
          <Box flex="1" color={colors.textMuted}>{r.label}</Box>
          {showBefore && <Box flex="1" color={colors.textMuted}>{r.before ?? <span style={{ opacity: 0.5 }}>—</span>}</Box>}
          <Box flex="1" color={colors.text} fontWeight="600">
            {r.afterNode ?? r.after ?? <span style={{ opacity: 0.5 }}>—</span>}
          </Box>
        </Flex>
      ))}
    </Box>
  )
}

function StagedFileDownload({ mrId, fileId, label }: { mrId: string; fileId: string; label: string }) {
  const [busy, setBusy] = useState(false)
  const handleClick = async () => {
    setBusy(true)
    try {
      const url = modificationRequestService.getStagedFileDownloadUrl(mrId, fileId)
      const response = await apiClient.fetchAuthorized(url)
      if (!response.ok) throw new Error(`Descarga falló: ${response.status}`)
      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = label
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'Error al descargar archivo' })
    } finally {
      setBusy(false)
    }
  }
  return (
    <Box as="button" type="button"
      display="inline-flex" alignItems="center" gap={1.5}
      px={2} py={1} borderRadius="md"
      bg={`${brand.green}10`} color={brand.green}
      fontSize="xs" fontWeight="600"
      cursor={busy ? 'not-allowed' : 'pointer'}
      opacity={busy ? 0.6 : 1}
      _hover={busy ? {} : { bg: `${brand.green}20` }}
      onClick={handleClick}
      disabled={busy}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
      </svg>
      {busy ? 'Descargando…' : 'Descargar archivo'}
    </Box>
  )
}

// ── Overlay helpers ──────────────────────────────────────────────────────────

interface StagedIgafom {
  id?: string; miningRightId?: string
  presentationDate?: string | null; expirationDate?: string | null
  fileNumber?: string | null; type?: number | null
  exploitationMethod?: number | null; documentStatus?: number | null; substanceId?: number | null
  productiveCapacity?: string | null
  documentFileAttachmentId?: string | null; receiptFileAttachmentId?: string | null
}

interface StagedLegalDoc {
  id?: string; miningRightId?: string
  documentType?: number | null
  presentationDate?: string | null; expirationDate?: string | null
  fileNumber?: string | null; documentStatus?: number | null
  documentFileAttachmentId?: string | null; receiptFileAttachmentId?: string | null
}

interface StagedProduction {
  miningRightId?: string
  dailyEstimatedProduction?: number | null
  period?: number | null; year?: number | null
  presentationDate?: string | null; fileNumber?: string | null
  totalMetallic?: number | null; totalNonMetallic?: number | null; expirationDate?: string | null
  documentFileAttachmentId?: string | null
}

interface StagedMiningRightComponent {
  miningRightId?: string
  componentTypeId?: string
  componentTypeName?: string | null
  name?: string | null
  latitude?: number; longitude?: number
  northing?: number; easting?: number
  isOutsidePolygon?: boolean
}

interface StagedMiningRightUpdate {
  usesExplosives?: boolean
  name?: string | null
  ubigeoCode?: string | null
  utmZone?: number | null
  boundaryWkt?: string
}

// Convierte un POLYGON WKT en WGS84 ("POLYGON((lng lat, lng lat, ...))") al GeoJSON
// string que consume el mapa/editor (geoJson). Devuelve null si no parsea.
function wktPolygonToGeoJson(wkt: string): string | null {
  const m = wkt.match(/POLYGON\s*\(\s*\(([^)]+)\)\s*\)/i)
  if (!m || !m[1]) return null
  const ring: number[][] = []
  for (const pair of m[1].split(',')) {
    const [lngStr, latStr] = pair.trim().split(/\s+/)
    const lng = Number(lngStr); const lat = Number(latStr)
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue
    ring.push([lng, lat])
  }
  if (ring.length < 3) return null
  return JSON.stringify({ type: 'Polygon', coordinates: [ring] })
}

interface StagedBankAccount { accountNumber?: string; bankEntityId?: number; accountTypeId?: number }

interface StagedCompanyPerson {
  companyId?: string
  identityDoc?: string | null
  names?: string | null
  paternalSurname?: string | null
  maternalSurname?: string | null
  email?: string | null
  phoneMobile?: string | null
  contactType?: number
  documentType?: number
  gender?: number
}

interface StagedTaxReport {
  companyBusinessId?: string
  code?: string | null
  issueDate?: string
  documentFileAttachmentId?: string | null
}

function applyTaxReportsOverlay(taxReports: TaxReportResponse[], changes: DraftChangeResponse[]): TaxReportResponse[] {
  const txChanges = changes.filter(c => c.entityName === 'TaxReport')
  if (txChanges.length === 0) return taxReports
  const result = [...taxReports]
  for (const change of txChanges.filter(c => c.operation === 0)) {
    let parsed: StagedTaxReport = {}
    try { parsed = JSON.parse(change.proposedSnapshotJson) as StagedTaxReport } catch { /* */ }
    if (!parsed.issueDate) continue
    const iso = parsed.issueDate
    // Vigencia fija 10 dias — calculamos expiracion al vuelo.
    const exp = new Date(iso)
    exp.setDate(exp.getDate() + 10)
    const expIso = exp.toISOString().slice(0, 10)
    const today = new Date().toISOString().slice(0, 10)
    result.unshift({
      id: `staged-${change.id}`,
      companyBusinessId: parsed.companyBusinessId ?? '',
      code: parsed.code ?? null,
      issueDate: iso,
      expirationDate: expIso,
      isValid: expIso >= today,
      documentFile: stagedFileRef(change.modificationRequestId, parsed.documentFileAttachmentId),
      createdAt: new Date().toISOString(),
    })
  }
  return result
}

interface StagedPowerValidity {
  companyBusinessId?: string
  companyPersonId?: string | null
  companyPersonDraftChangeId?: string | null
  code?: string
  issueDate?: string
  documentFileAttachmentId?: string | null
}

function applyPowerValiditiesOverlay(pv: PowerValidityResponse[], changes: DraftChangeResponse[], persons: CompanyPersonResponse[]): PowerValidityResponse[] {
  const pvChanges = changes.filter(c => c.entityName === 'PowerValidity')
  if (pvChanges.length === 0) return pv
  const result = [...pv]
  for (const change of pvChanges.filter(c => c.operation === 0)) {
    let parsed: StagedPowerValidity = {}
    try { parsed = JSON.parse(change.proposedSnapshotJson) as StagedPowerValidity } catch { /* */ }
    if (!parsed.issueDate) continue
    const exp = new Date(parsed.issueDate)
    exp.setDate(exp.getDate() + 10)
    const expIso = exp.toISOString().slice(0, 10)
    const today = new Date().toISOString().slice(0, 10)
    const personId = parsed.companyPersonId ?? (parsed.companyPersonDraftChangeId ? `staged-${parsed.companyPersonDraftChangeId}` : '')
    result.unshift({
      id: `staged-${change.id}`,
      companyBusinessId: parsed.companyBusinessId ?? '',
      companyPersonId: personId,
      personFullName: persons.find(p => p.id === personId)?.fullName ?? null,
      code: parsed.code ?? '',
      issueDate: parsed.issueDate,
      expirationDate: expIso,
      isValid: expIso >= today,
      documentFile: stagedFileRef(change.modificationRequestId, parsed.documentFileAttachmentId),
      createdAt: new Date().toISOString(),
    })
  }
  return result
}

interface StagedShareholder {
  companyBusinessId?: string
  documentType?: number
  documentNumber?: string
  firstName?: string
  lastName?: string
  participationPercent?: number
  certificateFileAttachmentId?: string | null
  certificateFileName?: string | null
  validationStatus?: number
  validationStatusName?: string
  validationMessage?: string | null
}

function applyShareholdersOverlay(base: ShareholderResponse[], changes: DraftChangeResponse[]): ShareholderResponse[] {
  const shChanges = changes.filter(c => c.entityName === 'SupplierShareholder')
  if (shChanges.length === 0) return base

  const toResponse = (p: StagedShareholder, id: string): ShareholderResponse => ({
    id,
    documentType: p.documentType ?? 1,
    documentNumber: p.documentNumber ?? '',
    firstName: p.firstName ?? '',
    lastName: p.lastName ?? '',
    fullName: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim(),
    participationPercent: p.participationPercent ?? 0,
    certificateFile: p.certificateFileName
      ? { id: p.certificateFileAttachmentId ?? 'staged', originalFileName: p.certificateFileName, storagePath: '', contentType: 'application/pdf', extension: null, fileSize: 0, uploadedAt: new Date().toISOString() }
      : null,
    validationStatus: p.validationStatus ?? 0,
    validationStatusName: p.validationStatusName ?? 'PENDIENTE',
    validationMessage: p.validationMessage ?? null,
    hasTitle: false, hasMtpeSignature: false,
    certificateExpirationDate: null, detectedDocumentNumber: null,
    policeClean: false, judicialClean: false, criminalClean: false,
    validatedAt: null,
  })

  const parse = (json: string): StagedShareholder => { try { return JSON.parse(json) as StagedShareholder } catch { return {} } }

  // Deletes y updates por entityId.
  const deletedIds = new Set(shChanges.filter(c => c.operation === 2 && c.entityId).map(c => c.entityId as string))
  const updateById = new Map(shChanges.filter(c => c.operation === 1 && c.entityId).map(c => [c.entityId as string, c]))

  let result = base
    .filter(s => !deletedIds.has(s.id))
    .map(s => {
      const upd = updateById.get(s.id)
      return upd ? { ...toResponse(parse(upd.proposedSnapshotJson), s.id), id: s.id } : s
    })

  // Inserts (staged-{id}).
  for (const change of shChanges.filter(c => c.operation === 0)) {
    result = [toResponse(parse(change.proposedSnapshotJson), `staged-${change.id}`), ...result]
  }
  return result
}

function applyPersonsOverlay(persons: CompanyPersonResponse[], changes: DraftChangeResponse[]): CompanyPersonResponse[] {
  const personChanges = changes.filter(c => c.entityName === 'CompanyPerson')
  if (personChanges.length === 0) return persons
  const result: CompanyPersonResponse[] = []
  for (const p of persons) {
    const change = personChanges.find(c => c.entityId === p.id)
    if (change?.operation === 2) continue // staged delete
    result.push(p)
  }
  for (const change of personChanges.filter(c => c.operation === 0)) {
    let parsed: StagedCompanyPerson = {}
    try { parsed = JSON.parse(change.proposedSnapshotJson) as StagedCompanyPerson } catch { /* */ }
    const fullName = [parsed.names, parsed.paternalSurname, parsed.maternalSurname].filter(Boolean).join(' ')
    result.push({
      id: `staged-${change.id}`,
      personId: `staged-${change.id}`,
      fullName,
      identityDoc: parsed.identityDoc ?? null,
      contactType: parsed.contactType ?? 0,
      // Sin nombre la tarjeta mostraba " · DNI …" sin decir qué tipo de contacto es.
      contactTypeName: parsed.contactType != null ? CONTACT_TYPE_LABELS[Number(parsed.contactType)] ?? null : null,
      email: parsed.email ?? null,
      phone: parsed.phoneMobile ?? null,
    })
  }
  return result
}

interface StagedMiningSupplier {
  categoryId?: number; incomeTaxCategoryId?: number; legalCondition?: number
  activityStartDate?: string | null; reinfoInscriptionDate?: string | null; reinfoStatus?: string | null
  productiveCapacity?: string | null; splaftDeclared?: boolean
  splaftDocumentFileAttachmentId?: string | null
}

/** Etiquetas de un IGAFOM stageado; los campos no tocados conservan la del registro. */
function igafomLabels(parsed: StagedIgafom, rec: IgafomRecordResponse | null) {
  return {
    typeName: parsed.type != null ? enumLabel(IGAFOM_TYPES, parsed.type) : rec?.typeName ?? null,
    documentStatusName: parsed.documentStatus != null ? enumLabel(IGAFOM_DOCUMENT_STATUSES, parsed.documentStatus) : rec?.documentStatusName ?? null,
    substanceName: parsed.substanceId != null ? enumLabel(MINERAL_SUBSTANCES, parsed.substanceId) : rec?.substanceName ?? null,
  }
}

function applyMiningRightsOverlay(miningRights: MiningRightResponse[], changes: DraftChangeResponse[]): MiningRightResponse[] {
  let result = miningRights

  // IGAFOM overlay
  const igafomChanges = changes.filter(c => c.entityName === 'IgafomRecord')
  if (igafomChanges.length > 0) {
    result = result.map(mr => {
      const existing = mr.igafomRecords ?? []
      const records: IgafomRecordResponse[] = []
      for (const rec of existing) {
        const change = igafomChanges.find(c => c.entityId === rec.id)
        if (!change) { records.push(rec); continue }
        if (change.operation === 2) continue
        if (change.operation === 1) {
          let parsed: StagedIgafom = {}
          try { parsed = JSON.parse(change.proposedSnapshotJson) as StagedIgafom } catch { /* */ }
          records.push({
            ...rec,
            presentationDate: parsed.presentationDate ?? rec.presentationDate,
            expirationDate: parsed.expirationDate ?? rec.expirationDate,
            fileNumber: parsed.fileNumber ?? rec.fileNumber,
            type: parsed.type ?? rec.type,
            exploitationMethod: parsed.exploitationMethod ?? rec.exploitationMethod,
            documentStatus: parsed.documentStatus ?? rec.documentStatus,
            substanceId: parsed.substanceId ?? rec.substanceId,
            productiveCapacity: parsed.productiveCapacity ?? rec.productiveCapacity,
            // La tarjeta pinta los *Name: sin recalcularlos seguía mostrando el tipo, estado
            // y sustancia ANTERIORES aunque el cambio estuviera stageado.
            ...igafomLabels(parsed, rec),
            receiptFile: stagedFileRef(change.modificationRequestId, parsed.receiptFileAttachmentId) ?? rec.receiptFile,
            documentFile: stagedFileRef(change.modificationRequestId, parsed.documentFileAttachmentId) ?? rec.documentFile,
          } as IgafomRecordResponse)
        }
      }
      for (const change of igafomChanges.filter(c => c.operation === 0)) {
        let parsed: StagedIgafom = {}
        try { parsed = JSON.parse(change.proposedSnapshotJson) as StagedIgafom } catch { /* */ }
        if (parsed.miningRightId !== mr.id) continue
        records.push({
          id: `staged-${change.id}`,
          presentationDate: parsed.presentationDate ?? undefined,
          expirationDate: parsed.expirationDate ?? undefined,
          fileNumber: parsed.fileNumber ?? undefined,
          type: parsed.type ?? undefined,
          exploitationMethod: parsed.exploitationMethod ?? undefined,
          documentStatus: parsed.documentStatus ?? undefined,
          substanceId: parsed.substanceId ?? undefined,
          productiveCapacity: parsed.productiveCapacity ?? undefined,
          ...igafomLabels(parsed, null),
          receiptFile: stagedFileRef(change.modificationRequestId, parsed.receiptFileAttachmentId),
          documentFile: stagedFileRef(change.modificationRequestId, parsed.documentFileAttachmentId),
        } as IgafomRecordResponse)
      }
      return { ...mr, igafomRecords: records }
    })
  }

  // LegalDocument overlay (espejo de IGAFOM)
  const legalDocChanges = changes.filter(c => c.entityName === 'LegalDocumentRecord')
  if (legalDocChanges.length > 0) {
    result = result.map(mr => {
      const existing = mr.legalDocumentRecords ?? []
      const records: LegalDocumentRecordResponse[] = []
      for (const rec of existing) {
        const change = legalDocChanges.find(c => c.entityId === rec.id)
        if (!change) { records.push(rec); continue }
        if (change.operation === 2) continue
        if (change.operation === 1) {
          let parsed: StagedLegalDoc = {}
          try { parsed = JSON.parse(change.proposedSnapshotJson) as StagedLegalDoc } catch { /* */ }
          records.push({
            ...rec,
            documentType: parsed.documentType ?? rec.documentType,
            presentationDate: parsed.presentationDate ?? rec.presentationDate,
            expirationDate: parsed.expirationDate ?? rec.expirationDate,
            fileNumber: parsed.fileNumber ?? rec.fileNumber,
            documentStatus: parsed.documentStatus ?? rec.documentStatus,
            documentTypeName: parsed.documentType != null ? enumLabel(LEGAL_DOCUMENT_TYPES, parsed.documentType) : rec.documentTypeName,
            documentStatusName: parsed.documentStatus != null ? enumLabel(IGAFOM_DOCUMENT_STATUSES, parsed.documentStatus) : rec.documentStatusName,
            receiptFile: stagedFileRef(change.modificationRequestId, parsed.receiptFileAttachmentId) ?? rec.receiptFile,
            documentFile: stagedFileRef(change.modificationRequestId, parsed.documentFileAttachmentId) ?? rec.documentFile,
          } as LegalDocumentRecordResponse)
        }
      }
      for (const change of legalDocChanges.filter(c => c.operation === 0)) {
        let parsed: StagedLegalDoc = {}
        try { parsed = JSON.parse(change.proposedSnapshotJson) as StagedLegalDoc } catch { /* */ }
        if (parsed.miningRightId !== mr.id) continue
        records.push({
          id: `staged-${change.id}`,
          miningRightId: mr.id,
          documentType: parsed.documentType ?? null,
          documentTypeName: parsed.documentType != null ? enumLabel(LEGAL_DOCUMENT_TYPES, parsed.documentType) : null,
          presentationDate: parsed.presentationDate ?? null,
          expirationDate: parsed.expirationDate ?? null,
          fileNumber: parsed.fileNumber ?? null,
          documentStatus: parsed.documentStatus ?? null,
          documentStatusName: parsed.documentStatus != null ? enumLabel(IGAFOM_DOCUMENT_STATUSES, parsed.documentStatus) : null,
          receiptFile: stagedFileRef(change.modificationRequestId, parsed.receiptFileAttachmentId),
          documentFile: stagedFileRef(change.modificationRequestId, parsed.documentFileAttachmentId),
        })
      }
      return { ...mr, legalDocumentRecords: records }
    })
  }

  // Production overlay
  const prodChanges = changes.filter(c => c.entityName === 'ProductionDeclaration')
  if (prodChanges.length > 0) {
    result = result.map(mr => {
      const existing = mr.productionDeclarations ?? []
      const records: ProductionDeclarationResponse[] = []
      for (const rec of existing) {
        const change = prodChanges.find(c => c.entityId === rec.id)
        if (!change) { records.push(rec); continue }
        if (change.operation === 2) continue
        records.push(rec)
      }
      for (const change of prodChanges.filter(c => c.operation === 0)) {
        let parsed: StagedProduction = {}
        try { parsed = JSON.parse(change.proposedSnapshotJson) as StagedProduction } catch { /* */ }
        if (parsed.miningRightId !== mr.id) continue
        records.push({
          id: `staged-${change.id}`,
          period: parsed.period ?? undefined,
          year: parsed.year ?? undefined,
          presentationDate: parsed.presentationDate ?? undefined,
          fileNumber: parsed.fileNumber ?? undefined,
          dailyEstimatedProduction: parsed.dailyEstimatedProduction ?? undefined,
          totalMetallic: parsed.totalMetallic ?? undefined,
          totalNonMetallic: parsed.totalNonMetallic ?? undefined,
          expirationDate: parsed.expirationDate ?? undefined,
          documentFile: stagedFileRef(change.modificationRequestId, parsed.documentFileAttachmentId),
        } as ProductionDeclarationResponse)
      }
      return { ...mr, productionDeclarations: records }
    })
  }

  // Component overlay
  const compChanges = changes.filter(c => c.entityName === 'MiningRightComponent')
  if (compChanges.length > 0) {
    result = result.map(mr => {
      const existing = mr.components ?? []
      const comps: MiningRightComponentResponseItem[] = []
      for (const c of existing) {
        const change = compChanges.find(ch => ch.entityId === c.id)
        if (change?.operation === 2) continue
        comps.push(c)
      }
      for (const change of compChanges.filter(c => c.operation === 0)) {
        let parsed: StagedMiningRightComponent = {}
        try { parsed = JSON.parse(change.proposedSnapshotJson) as StagedMiningRightComponent } catch { /* */ }
        if (parsed.miningRightId !== mr.id) continue
        // El editor (ComponentsEditor) usa northing/easting (UTM) para la lista y para
        // convertir a lat/lng en el mapa; sin ellos mostraba "N: NaN" y sin marcador.
        comps.push({
          id: `staged-${change.id}`,
          componentTypeId: parsed.componentTypeId ?? '',
          componentTypeName: parsed.componentTypeName ?? null,
          componentTypeCode: null,
          categoryCode: null,
          name: parsed.name ?? null,
          northing: parsed.northing ?? NaN,
          easting: parsed.easting ?? NaN,
          isOutsidePolygon: parsed.isOutsidePolygon ?? false,
        } as MiningRightComponentResponseItem)
      }
      return { ...mr, components: comps }
    })
  }

  // MiningRight scalar overlay (UPDATE del DM): polígono (boundaryWkt→geoJson), zona UTM,
  // uso de explosivos y otros campos. Sin aplicar el polígono, el mapa y el modal de edición
  // seguían mostrando el polígono ANTIGUO porque el nuevo no se persiste hasta aprobar.
  const mrUpdateChanges = changes.filter(c => c.entityName === 'MiningRight' && c.operation === 1)
  if (mrUpdateChanges.length > 0) {
    result = result.map(mr => {
      const change = mrUpdateChanges.find(c => c.entityId === mr.id)
      if (!change) return mr
      let parsed: StagedMiningRightUpdate = {}
      try { parsed = JSON.parse(change.proposedSnapshotJson) as StagedMiningRightUpdate } catch { /* */ }
      const next = { ...mr }
      if (parsed.usesExplosives != null) next.usesExplosives = parsed.usesExplosives
      if (parsed.name != null) next.name = parsed.name
      if (parsed.ubigeoCode != null) next.ubigeoCode = parsed.ubigeoCode
      if (parsed.utmZone != null) next.utmZone = parsed.utmZone
      if (parsed.boundaryWkt) {
        const geo = wktPolygonToGeoJson(parsed.boundaryWkt)
        if (geo) {
          next.geoJson = geo
          next.hasBoundary = true
          // El aviso "concesión fuera del derecho minero" y el encuadre deben evaluar el
          // polígono PROPUESTO, no el vigente.
          const rings = referenceRings(mr.officialGeoJson)
          if (rings.length > 0) next.concessionWithinOfficial = countOutside(referenceRings(geo).flat(), rings) === 0
        }
      }
      return next
    })
  }

  // ExplosiveAuthorization overlay (insert/update/delete por D.M.)
  const authChanges = changes.filter(c => c.entityName === 'ExplosiveAuthorization')
  if (authChanges.length > 0) {
    result = result.map(mr => {
      const existing = mr.explosiveAuthorizations ?? []
      const records: ExplosiveAuthorizationResponse[] = []
      for (const rec of existing) {
        const change = authChanges.find(c => c.entityId === rec.id)
        if (!change) { records.push(rec); continue }
        if (change.operation === 2) continue
        if (change.operation === 1) {
          let parsed: StagedExplosiveAuth = {}
          try { parsed = JSON.parse(change.proposedSnapshotJson) as StagedExplosiveAuth } catch { /* */ }
          records.push({
            ...rec, ...stagedAuthFields(parsed),
            authorizationTypeName: enumLabel(EXPLOSIVE_AUTHORIZATION_TYPES, parsed.authorizationType ?? rec.authorizationType),
            documentFile: stagedFileRef(change.modificationRequestId, parsed.documentFileAttachmentId, parsed.documentFileName) ?? rec.documentFile,
          })
        }
      }
      for (const change of authChanges.filter(c => c.operation === 0)) {
        let parsed: StagedExplosiveAuth = {}
        try { parsed = JSON.parse(change.proposedSnapshotJson) as StagedExplosiveAuth } catch { /* */ }
        if (parsed.miningRightId !== mr.id) continue
        records.push({ id: `staged-${change.id}`, miningRightId: mr.id, authorizationTypeName: enumLabel(EXPLOSIVE_AUTHORIZATION_TYPES, parsed.authorizationType ?? 1), documentFile: stagedFileRef(change.modificationRequestId, parsed.documentFileAttachmentId, parsed.documentFileName), ...stagedAuthFields(parsed) } as ExplosiveAuthorizationResponse)
      }
      return { ...mr, explosiveAuthorizations: records }
    })
  }

  // ExplosiveHandler overlay (insert/delete por D.M.)
  const handlerChanges = changes.filter(c => c.entityName === 'ExplosiveHandler')
  if (handlerChanges.length > 0) {
    result = result.map(mr => {
      const existing = mr.explosiveHandlers ?? []
      const records: ExplosiveHandlerResponse[] = []
      for (const rec of existing) {
        const change = handlerChanges.find(c => c.entityId === rec.id)
        if (change?.operation === 2) continue
        records.push(rec)
      }
      for (const change of handlerChanges.filter(c => c.operation === 0)) {
        let parsed: StagedExplosiveHandler = {}
        try { parsed = JSON.parse(change.proposedSnapshotJson) as StagedExplosiveHandler } catch { /* */ }
        if (parsed.miningRightId !== mr.id) continue
        const fullName = [parsed.names, parsed.paternalSurname, parsed.maternalSurname].filter(Boolean).join(' ')
        records.push({
          id: `staged-${change.id}`,
          miningRightId: mr.id,
          personId: '',
          fullName,
          identityDoc: parsed.identityDoc ?? null,
          licenseNumber: parsed.licenseNumber ?? null,
          expirationDate: parsed.expirationDate ?? null,
          sucamecValidated: parsed.sucamecValidated ?? false,
          sucamecValidatedAt: null,
          status: 'unknown',
        })
      }
      return { ...mr, explosiveHandlers: records }
    })
  }

  return result
}

interface StagedExplosiveAuth {
  miningRightId?: string; authorizationType?: number; resolutionNumber?: string | null
  documentFileAttachmentId?: string | null; documentFileName?: string | null
  emissionDate?: string | null; expirationDate?: string | null
  authorizedExplosives?: string | null; maxCapacity?: string | null
}

function stagedAuthFields(p: StagedExplosiveAuth): Partial<ExplosiveAuthorizationResponse> {
  return {
    authorizationType: p.authorizationType ?? 1,
    resolutionNumber: p.resolutionNumber ?? null,
    emissionDate: p.emissionDate ?? null,
    expirationDate: p.expirationDate ?? null,
    authorizedExplosives: p.authorizedExplosives ?? null,
    maxCapacity: p.maxCapacity ?? null,
  }
}

interface StagedExplosiveHandler {
  miningRightId?: string; identityDoc?: string | null; names?: string | null
  paternalSurname?: string | null; maternalSurname?: string | null
  licenseNumber?: string | null; expirationDate?: string | null; sucamecValidated?: boolean
}

function applyContractOverlay(
  active: SupplierContractResponse | null,
  history: SupplierContractResponse[],
  changes: DraftChangeResponse[],
): { active: SupplierContractResponse | null; history: SupplierContractResponse[] } {
  const change = changes.find(c => c.entityName === 'SupplierContract' && c.operation === 0)
  if (!change) return { active, history }
  let parsed: { contractDate?: string | null; expirationDate?: string | null; notes?: string | null; documentFileName?: string | null } = {}
  try { parsed = JSON.parse(change.proposedSnapshotJson) } catch { /* */ }
  const staged: SupplierContractResponse = {
    id: `staged-${change.id}`,
    contractDate: parsed.contractDate ?? null,
    expirationDate: parsed.expirationDate ?? null,
    notes: parsed.notes ?? null,
    isActive: true,
    documentFile: parsed.documentFileName
      ? ({ originalFileName: parsed.documentFileName, storagePath: '' } as unknown as SupplierContractResponse['documentFile'])
      : null,
    createdAt: '',
  }
  // El contrato vigente pasa al frente del historial (igual que al aprobar).
  const newHistory = active ? [active, ...history] : history
  return { active: staged, history: newHistory }
}

function applyBankAccountOverlay(accounts: BankAccountResponse[], changes: DraftChangeResponse[]): BankAccountResponse[] {
  const bankChanges = changes.filter(c => c.entityName === 'BankAccount')
  if (bankChanges.length === 0) return accounts
  const result: BankAccountResponse[] = []
  for (const acc of accounts) {
    const change = bankChanges.find(c => c.entityId === acc.id)
    if (!change) { result.push(acc); continue }
    if (change.operation === 2) continue
    if (change.operation === 1) {
      let parsed: StagedBankAccount = {}
      try { parsed = JSON.parse(change.proposedSnapshotJson) as StagedBankAccount } catch { /* */ }
      const bankId = parsed.bankEntityId ?? acc.bankEntityId
      const typeId = parsed.accountTypeId ?? acc.accountTypeId
      result.push({
        ...acc,
        accountNumber: parsed.accountNumber ?? acc.accountNumber,
        bankEntityId: bankId, bankEntityName: enumLabel(BANK_ENTITIES, bankId),
        accountTypeId: typeId, accountTypeName: enumLabel(ACCOUNT_TYPES, typeId),
      })
    }
  }
  for (const change of bankChanges.filter(c => c.operation === 0)) {
    let parsed: StagedBankAccount = {}
    try { parsed = JSON.parse(change.proposedSnapshotJson) as StagedBankAccount } catch { /* */ }
    const bankId = parsed.bankEntityId ?? 0
    const typeId = parsed.accountTypeId ?? 0
    result.push({
      id: `staged-${change.id}`,
      accountNumber: parsed.accountNumber ?? '',
      bankEntityId: bankId, bankEntityName: enumLabel(BANK_ENTITIES, bankId),
      accountTypeId: typeId, accountTypeName: enumLabel(ACCOUNT_TYPES, typeId),
    } as BankAccountResponse)
  }
  return result
}

function applyMiningSupplierOverlay(proveedor: ProveedorResponse | null, changes: DraftChangeResponse[]): StagedMiningSupplier {
  const base: StagedMiningSupplier = {
    categoryId: proveedor?.miningSupplier?.categoryId ?? undefined,
    incomeTaxCategoryId: proveedor?.miningSupplier?.incomeTaxCategoryId ?? undefined,
    productiveCapacity: proveedor?.productiveCapacity ?? undefined,
    splaftDeclared: proveedor?.splaftDeclared ?? undefined,
    legalCondition: proveedor?.legalCondition ?? undefined,
    reinfoStatus: proveedor?.reinfoStatus ?? undefined,
  }
  const msChanges = changes.filter(c => c.entityName === 'MiningSupplier' && c.operation === 1)
  for (const change of msChanges) {
    try {
      const parsed = JSON.parse(change.proposedSnapshotJson) as StagedMiningSupplier
      Object.assign(base, parsed)
    } catch { /* */ }
  }
  return base
}

function extractCurrentMiningSupplierStaging(changes: DraftChangeResponse[]): StagedMiningSupplier {
  const msChange = changes.find(c => c.entityName === 'MiningSupplier' && c.operation === 1)
  if (!msChange) return {}
  try { return JSON.parse(msChange.proposedSnapshotJson) as StagedMiningSupplier } catch { return {} }
}

function StatusBadge({ status }: { status: number }) {
  const cfg: Record<number, { label: string; bg: string; color: string }> = {
    0: { label: 'BORRADOR', bg: 'rgba(107, 114, 128, 0.15)', color: '#857b71' },
    1: { label: 'PENDIENTE', bg: 'rgba(212, 154, 42, 0.15)', color: '#a8740e' },
    2: { label: 'APROBADA', bg: 'rgba(42, 138, 135, 0.15)', color: '#1d6664' },
    3: { label: 'RECHAZADA', bg: 'rgba(200, 68, 47, 0.15)', color: '#b23a26' },
    4: { label: 'CANCELADA', bg: 'rgba(107, 114, 128, 0.15)', color: '#857b71' },
  }
  const s = cfg[status] ?? cfg[0]!
  return (
    <Box px={2} py={0.5} borderRadius="md" fontSize="10px" fontWeight="bold" bg={s.bg} color={s.color}>
      {s.label}
    </Box>
  )
}
